import { getLocalizedMessage } from '@pimo/pimo-l10n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TIMEFRAMES, CONTRAST_TEXT_COLORS, ICONS, COLORS, ITEM_NAMES, PROJECT_NAMES } from 'product-test-types';

function buildAccessString(obj) {
    const { client, tool, role, projectIds, granularPermissions, } = obj;
    const qp = new URLSearchParams();
    qp.set("tool", tool);
    role && qp.set("role", role);
    if (granularPermissions && granularPermissions.length > 0) {
        granularPermissions.forEach((perm) => {
            qp.append("object", perm.object);
            const ops = perm.operation.split(",");
            ops.forEach((op) => qp.append("operation", op.trim()));
        });
    }
    let query = qp.toString();
    // Keep Keycloak group names short by storing projectIds as one CSV value.
    if (projectIds?.length) {
        const unique = Array.from(new Set(projectIds.map(String)));
        query += `&projectId=${unique.join(",")}`;
    }
    return `pimo-product/${client}?${query}`;
}
function parseArrayParam(raw) {
    if (!raw)
        return [];
    const trimmed = raw.trim();
    try {
        const asJson = JSON.parse(trimmed);
        if (Array.isArray(asJson)) {
            return asJson.map(String);
        }
    }
    catch { }
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        const inner = trimmed.slice(1, -1).trim();
        if (!inner)
            return [];
        return inner
            .split(",")
            .map((s) => s.trim().replace(/^"|"$/g, "").replace(/^'|'$/g, ""));
    }
    return trimmed.split(",").map((s) => s.trim());
}
function parsePermission(str) {
    const raw = typeof str === "string" ? str : "";
    const [path = "", query = ""] = raw.split("?");
    const [product, client] = path.split("/");
    const sp = new URLSearchParams(query);
    const tool = sp.get("tool") ?? "";
    const role = (sp.get("role") ?? "");
    const granularPermissions = [];
    let pendingOps = []; // operations seen before the first object
    let currentObject = null;
    let currentOps = [];
    for (const [key, value] of Array.from(sp.entries())) {
        if (key === "object") {
            if (currentObject) {
                granularPermissions.push({
                    object: currentObject,
                    operation: currentOps.join(","),
                });
            }
            currentObject = value;
            // consume any operations that appeared before this object
            currentOps = pendingOps;
            pendingOps = [];
        }
        else if (key === "operation") {
            if (currentObject) {
                currentOps.push(value);
            }
            else {
                pendingOps.push(value);
            }
        }
    }
    if (currentObject) {
        granularPermissions.push({
            object: currentObject,
            operation: currentOps.join(","),
        });
    }
    // Prefer repeated params; also expand compact CSV values.
    let projectIds = sp.getAll("projectId").flatMap((v) => parseArrayParam(v));
    // Fallback: if not repeated, try single-value legacy formats
    if (projectIds?.length === 0) {
        const raw = sp.get("projectId");
        projectIds = parseArrayParam(raw);
    }
    const result = { product, client, tool, role };
    if (projectIds?.length)
        result.projectIds = Array.from(new Set(projectIds.map(String)));
    if (granularPermissions.length > 0) {
        result.granularPermissions = granularPermissions;
    }
    else {
        let operationOps = sp.getAll("operation");
        let object = sp.getAll("object");
        if (operationOps?.length === 0) {
            const raw = sp.get("operation");
            operationOps = parseArrayParam(raw);
        }
        if (object?.length === 0) {
            const raw = sp.get("object");
            object = parseArrayParam(raw);
        }
    }
    return result;
}

function canUserPerformOperation(params) {
    const { userGroups, entityId = "", operation = "read", lowestLevelAccessRequiredToPerformThisOperation, tool, client, } = params;
    let userPermission = undefined;
    for (const group of userGroups ?? []) {
        const permissions = parsePermission(group);
        if (permissions.tool === tool && permissions.client === client) {
            userPermission = permissions;
        }
    }
    if (lowestLevelAccessRequiredToPerformThisOperation) {
        const levels = ["project", "program", "admin"];
        if (levels.indexOf(userPermission?.role ?? "project") <
            levels.indexOf(lowestLevelAccessRequiredToPerformThisOperation)) {
            return false;
        }
    }
    // Admins always have access
    if (userPermission?.role === "admin") {
        return true;
    }
    if (userPermission?.role === "program") {
        if (operation === "read") {
            return true;
        }
        if (operation &&
            params.object &&
            hasGranularOrLegacyAccess(userPermission, params.object, operation)) {
            return true;
        }
    }
    // Project role with matching entityId and operation, or any write access
    if (userPermission?.role === "project" &&
        operation &&
        userPermission?.projectIds) {
        if (userPermission.projectIds?.some((p) => String(p) === String(entityId)) &&
            params.object &&
            hasGranularOrLegacyAccess(userPermission, params.object, operation)) {
            return true;
        }
    }
    // Default: no access
    return false;
}
const hasGranularOrLegacyAccess = (userPermission, object, op) => {
    if (userPermission?.granularPermissions) {
        const perm = userPermission.granularPermissions.find((p) => p.object === object);
        return (perm?.operation
            .split(",")
            .map((s) => s.trim())
            .includes(op) ?? false);
    }
    return false;
};

function determineUserRole(user, tool) {
    if (!user) {
        return;
    }
    const userRoles = [];
    (user?.groups ?? []).forEach((group) => {
        const { tool: parsedTool, role } = parsePermission(group);
        if (parsedTool === tool && role != null) {
            userRoles.push(role);
        }
    });
    return determineRole(userRoles);
}
function determineRole(roles) {
    if (roles.includes("admin")) {
        return "admin";
    }
    if (roles.includes("program")) {
        return "program";
    }
    if (roles.includes("project")) {
        return "project";
    }
}

function calculateOverallCompletionDegreeCounts(subentities) {
    const completionDegreeCounts = {};
    for (const entry of subentities?.map((entry) => entry.degreeOfCompletion) ??
        []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(completionDegreeCounts).includes(entry)) {
            completionDegreeCounts[entry]++;
        }
        else {
            completionDegreeCounts[entry] = 1;
        }
    }
    return { completionDegreeCounts };
}

function calculateTotalBudgetDetails(projects) {
    const totalApprovedBudget = projects.reduce((total, project) => {
        return total + (project.approvedBudget ?? 0);
    }, 0);
    const totalSpentBudget = projects.reduce((total, project) => {
        return total + (project.spentBudget ?? 0);
    }, 0);
    const percentageOfBudgetSpent = totalApprovedBudget === 0
        ? 0
        : Math.round((totalSpentBudget / totalApprovedBudget) * 100);
    return {
        percentageOfBudgetSpent: percentageOfBudgetSpent,
        approvedBudget: totalApprovedBudget,
        spentBudget: totalSpentBudget,
    };
}

/**
 * Returns the stable public identifier for a custom field.
 *
 * We prefer `referenceId` once it exists, but keep falling back to the legacy
 * database `id` so older persisted configurations continue to resolve.
 */
function getCustomFieldPublicId(field) {
    const publicId = field?.referenceId ?? field?.id;
    return publicId == null ? "" : String(publicId);
}
/**
 * Builds the component id used in saved view configurations.
 *
 * For migrated fields we emit the stable reference-based id. For legacy fields
 * without a `referenceId` we keep the old `{id}-{label}` format so existing
 * saved layouts still match until they are rewritten.
 */
function getCustomFieldComponentId(field) {
    if (field?.referenceId) {
        return `customField-${getCustomFieldPublicId(field)}`;
    }
    if (field?.id != null && field?.label != null) {
        return `customField-${String(field.id)}-${String(field.label)}`;
    }
    return `customField-${getCustomFieldPublicId(field)}`;
}
/**
 * Builds the table column field name used by saved table configurations.
 *
 * Legacy configurations keyed custom columns by a normalized label, so we keep
 * returning that format until a stable `referenceId` exists.
 */
function getCustomFieldTableColumnFieldName(field) {
    if (!field?.referenceId && field?.label) {
        return getLegacyCustomFieldTableColumnFieldName(field.label);
    }
    return `customField-${getCustomFieldPublicId(field)}`;
}
/**
 * Reproduces the pre-referenceId column key format.
 *
 * This is used only for migration/compatibility lookups when reading old saved
 * table configurations.
 */
function getLegacyCustomFieldTableColumnFieldName(label) {
    const normalizedLabel = (label ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return `customField-${normalizedLabel || "unnamed"}`;
}
/**
 * Builds aggregation widget ids for dashboard/report cards.
 *
 * The helper emits the new stable-id form for migrated fields and preserves the
 * old id shapes for legacy fields so persisted widget references keep working.
 */
function getCustomFieldAggregationComponentId(type, field) {
    const publicId = getCustomFieldPublicId(field);
    const hasStableReferenceId = Boolean(field?.referenceId);
    const legacyId = field?.id == null ? publicId : String(field.id);
    const legacyLabel = String(field?.label ?? "");
    switch (type) {
        case "dashboard-item-sum":
            return hasStableReferenceId
                ? `customField-sum-item-${publicId}`
                : `customField-sum-item-${legacyId}`;
        case "dashboard-item-average":
            return hasStableReferenceId
                ? `customField-item-average-${publicId}`
                : `customField-item-average-${legacyId}`;
        case "dashboard-project-sum":
            return hasStableReferenceId
                ? `customField-project-sum-${publicId}`
                : `customField-project-sum-${legacyId}-${legacyLabel}`;
        case "dashboard-project-average":
            return hasStableReferenceId
                ? `customField-project-average-${publicId}`
                : `customField-project-average-${legacyId}-${legacyLabel}`;
        case "report-sum":
            return hasStableReferenceId
                ? `customField-sum-${publicId}`
                : `customField-sum-${legacyId}-${legacyLabel}`;
        case "report-average":
            return hasStableReferenceId
                ? `customField-average-${publicId}`
                : `customField-average-${legacyId}-${legacyLabel}`;
    }
}

const findDropdownOption = (options, value) => {
    if (!value) {
        return undefined;
    }
    return options?.find((option) => ("id" in option && option.id === value) || option.name === value);
};
const getDropdownOptionStoredValue = (option) => option?.id ?? option?.name;
const getDropdownOptionName = (options, value) => findDropdownOption(options, value)?.name ?? value ?? undefined;
const getDropdownOptionColor = (options, value) => findDropdownOption(options, value)?.color;
/** The stored value of the lowest-`order` option — e.g. the "Not Started" entry. */
const getFirstDropdownOptionStoredValue = (options) => getDropdownOptionStoredValue(options
    ?.slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .at(0));

const projectLimits = {
    customId: 15,
    name: 100,
    owner: 30,
    approvedBudget: 15,
    spentBudget: 15,
    description: 1500,
};
const basicItemLimits = {
    customId: 15,
    name: 100,
    owner: 30,
    description: 1500,
    budget: 15,
    comment: 1500,
};
const CHARACTER_LIMITATIONS = {
    programName: 100,
    programOwner: 30,
    programDescription: 1500,
    projectCustomId: projectLimits.customId,
    projectName: projectLimits.name,
    projectOwner: projectLimits.owner,
    projectDescription: projectLimits.description,
    approvedBudget: projectLimits.approvedBudget,
    "projects.{idx}.customId": projectLimits.customId,
    "projects.{idx}.name": projectLimits.name,
    "projects.{idx}.owner": projectLimits.owner,
    "projects.{idx}.approvedBudget": projectLimits.approvedBudget,
    "projects.{idx}.description": projectLimits.description,
    itemCustomId: basicItemLimits.customId,
    itemName: basicItemLimits.name,
    itemOwner: basicItemLimits.owner,
    itemDescription: basicItemLimits.description,
    itemComment: basicItemLimits.comment,
    "basicItems.{idx}.customId": basicItemLimits.customId,
    "basicItems.{idx}.name": basicItemLimits.name,
    "basicItems.{idx}.owner": basicItemLimits.owner,
    "basicItems.{idx}.description": basicItemLimits.description,
    "basicItems.{idx}.budget": basicItemLimits.budget,
    "basicItems.{idx}.comment": basicItemLimits.comment,
    "statusDefinition.name": 30,
    "degreeOfCompletionDefinition.name": 30,
    "customFields.label": 30,
    "customFields.options.name": 30,
    managementSummary: 1500,
    challenges: 1500,
    achievements: 1500,
    nextSteps: 1500,
    spentBudget: projectLimits.spentBudget,
    "reportingItems.{idx}.comment": 1500,
    "customFields.Select": 30,
    "customFields.Text": 100,
    "customFields.Number": 30,
    "customFields.{idx}.label": 30,
    "customFields.{idx}.options.{idx}.name": 30,
    "semaphoreStatusDefinition.{idx}.name": 30,
    "answerDefinition.{idx}.name": 30,
    "statusDefinition.{idx}.name": 30,
    "degreeOfCompletionDefinition.{idx}.name": 30,
    "maturityConfiguration.{idx}.name": 30,
    "dimensionDefinition.{idx}.name": 30,
    "dropdownFieldOneDefinition.{idx}.name": 30,
    "categoryDefinition.{idx}.name": 30,
    "secondCategoryDefinition.{idx}.name": 30,
};

const debounceHandlers = {};
/**
 * Debounced field validation utility.
 *
 * This function validates a form field or an item in an array of fields using the provided validation checks.
 * It supports debouncing to avoid excessive validation calls during rapid input changes.
 *
 * @param {Object} params - The parameters for field validation.
 * @param {string} [params.fieldName] - The name of the field to validate.
 * @param {string} [params.iterableFieldName] - The name of the iterable field (with "{idx}" as a placeholder for index).
 *   Use this when validating items in an array of fields. For example, if validating the third item in an array field called "emails",
 *   set iterableFieldName to "emails.{idx}" and idx to 2. The function will replace "{idx}" with the index, resulting in "emails.2" as the error key.
 *   Multiple "{idx}" placeholders can be used for nested arrays, and the idx parameter can be an array of indices to replace them in order.
 * @param {string} [params.customFieldKey] - Optional custom key for field-specific validation.
 * @param {number | number[]} [params.idx] - Index for iterable fields. Can be a single number or an array of numbers for multiple placeholders in the field name.
 * @param {unknown} [params.value] - The value to validate.
 * @param {Record<string, ExtendedFieldError | FieldError>} params.errors - Current error state object.
 * @param {(errors: Record<string, ExtendedFieldError | FieldError>) => void} params.setErrors - Callback to update the error state.
 * @param {string} [params.tabContainingErrors] - Optional tab identifier containing errors.
 * @param {("required" | "maxLength" )[]} [params.checks] - List of validation checks to perform. If omitted, all checks are run.
 * @param {string} [params.requiredMessage] - The error message to show when a required field is empty. If not provided, will attempt to fetch from locales.
 * @param {string} [params.maxLengthMessage] - The error message template for max length validation (should contain {maxLength} placeholder). If not provided, will attempt to fetch from locales.
 *
 * The function clears any existing debounce timer for the field, then sets a new timer.
 * On timer execution, it removes previous errors for the field, runs the specified validation checks
 * ("required", "maxLength") and updates the error state via setErrors.
 * - "required": Checks if the field is filled.
 * - "maxLength": Checks if the value does not exceed the maximum allowed length.
 */
const checkField = ({ fieldName, iterableFieldName, customFieldKey, idx, value, errors, setErrors, tabContainingErrors, checks, language, requiredMessage, maxLengthMessage, }) => {
    const resolvedFieldName = fieldName ??
        (iterableFieldName ? resolveIndex(iterableFieldName, idx) : undefined);
    if (!resolvedFieldName) {
        return;
    }
    if (debounceHandlers[resolvedFieldName]) {
        clearTimeout(debounceHandlers[resolvedFieldName]);
    }
    debounceHandlers[resolvedFieldName] = setTimeout(() => {
        if (fieldName) {
            delete errors[fieldName];
        }
        else if (iterableFieldName && idx != null) {
            delete errors[resolvedFieldName];
        }
        if (!checks || checks.includes("required")) {
            checkRequiredField({
                fieldName,
                iterableFieldName,
                idx,
                value,
                errors,
                tabContainingErrors,
                language,
                message: requiredMessage,
            });
        }
        if (!checks || checks.includes("maxLength")) {
            checkFieldLength({
                fieldName,
                iterableFieldName,
                idx,
                value,
                errors,
                customFieldKey,
                tabContainingErrors,
                language,
                messageTemplate: maxLengthMessage,
            });
        }
        setErrors(errors);
    }, 250);
};
/**
 * Validates that a field value is present (required field check).
 *
 * If the value is falsy (e.g., undefined, null, empty string), this function adds a "required" error
 * to the errors object for the specified field or iterable field item.
 *
 * @param {Object} params - The parameters for required field validation.
 * @param {string} [params.fieldName] - The name of the field to validate.
 * @param {string} [params.iterableFieldName] - The name of the iterable field (with "{idx}" as a placeholder for index).
 * @param {number} [params.idx] - Index for iterable fields.
 * @param {unknown} [params.value] - The value to check for presence.
 * @param {Record<string, ExtendedFieldError | FieldError>} params.errors - The error state object to update.
 * @param {string} [params.tabContainingErrors] - Optional tab identifier containing errors.
 * @param {string} [params.message] - The error message to show. If not provided, will attempt to fetch from locales.
 *
 * If the value is present, no action is taken. Otherwise, an error is added to the errors object.
 */
const checkRequiredField = ({ fieldName, iterableFieldName, idx, value, errors, tabContainingErrors, language = "en", message, }) => {
    const hasValue = value !== undefined && value !== null && value !== "";
    if (hasValue) {
        return;
    }
    const errorMessage = message || getBufferedTranslation(language).required;
    if (fieldName != null) {
        errors[fieldName] = {
            type: "required",
            message: errorMessage,
            tabContainingErrors,
        };
    }
    else if (iterableFieldName != null && idx != null) {
        errors[resolveIndex(iterableFieldName, idx)] = {
            type: "required",
            message: errorMessage,
            tabContainingErrors,
        };
    }
};
/**
 * Validates that a field value does not exceed its maximum allowed length.
 *
 * Converts the value to a string and checks its length against the configured maximum for the field.
 * If the value exceeds the limit, a "maxLength" error is added to the errors object for the field or iterable field item.
 *
 * @param {Object} params - The parameters for length validation.
 * @param {string} [params.fieldName] - The name of the field to validate.
 * @param {string} [params.iterableFieldName] - The name of the iterable field (with "{idx}" as a placeholder for index).
 * @param {number} [params.idx] - Index for iterable fields.
 * @param {unknown} [params.value] - The value to check length for.
 * @param {Record<string, ExtendedFieldError | FieldError>} params.errors - The error state object to update.
 * @param {string} [params.customFieldKey] - Optional custom key for field-specific length limits.
 * @param {string} [params.tabContainingErrors] - Optional tab identifier containing errors.
 * @param {string} [params.messageTemplate] - The error message template (should contain {maxLength} placeholder). If not provided, will attempt to fetch from locales.
 *
 * If the value is empty or within the allowed length, no action is taken. Otherwise, an error is added to the errors object.
 */
const checkFieldLength = ({ fieldName, iterableFieldName, idx, value, errors, customFieldKey, tabContainingErrors, language = "en", messageTemplate, }) => {
    if (!value) {
        return;
    }
    // convert value to string to check the length
    const v = new String(value);
    if (fieldName != null) {
        const maxLength = CHARACTER_LIMITATIONS[customFieldKey ? customFieldKey : fieldName];
        const template = messageTemplate || getBufferedTranslation(language).maxLength;
        const message = template.replace("{maxLength}", `${maxLength}`);
        if (v.length > maxLength) {
            errors[fieldName] = {
                type: "maxLength",
                message,
                tabContainingErrors,
            };
        }
    }
    else if (iterableFieldName != null && idx != null) {
        const maxLength = CHARACTER_LIMITATIONS[customFieldKey ? customFieldKey : iterableFieldName];
        const template = messageTemplate || getBufferedTranslation(language).maxLength;
        const message = template.replace("{maxLength}", `${maxLength}`);
        if (v.length > maxLength) {
            errors[resolveIndex(iterableFieldName, idx)] = {
                type: "maxLength",
                message,
                tabContainingErrors,
            };
        }
    }
};
/**
 * Utility function to resolve the error key for an iterable field item based on the provided field name and index.
 *
 * This function takes an iterable field name with a "{idx}" placeholder and replaces it with the provided index or indices.
 * If no index is provided, it returns the original iterable field name.
 *
 * @param {string} iterableFieldName - The name of the iterable field containing the "{idx}" placeholder.
 * @param {number | number[]} [idx] - The index or indices to replace in the field name. Can be a single number or an array of numbers for multiple placeholders.
 * @returns {string} The resolved field name with the index(es) replaced, or the original field name if no index is provided.
 */
const resolveIndex = (iterableFieldName, idx) => {
    if (idx === undefined) {
        return iterableFieldName;
    }
    if (Array.isArray(idx)) {
        let name = iterableFieldName;
        idx.forEach((i) => {
            name = name.replace("{idx}", `${i}`);
        });
        return name;
    }
    return iterableFieldName.replace("{idx}", `${idx}`);
};
let bufferedLanguage;
let bufferedMessages;
const getBufferedTranslation = (language) => {
    if (bufferedLanguage === language && bufferedMessages) {
        return bufferedMessages;
    }
    const messages = {
        maxLength: getLocalizedMessage(language, "validationErrors.maxLengthWithRemainingPlaceholder"),
        required: getLocalizedMessage(language, "validationErrors.fieldRequired"),
    };
    bufferedLanguage = language;
    bufferedMessages = messages;
    return messages;
};

/**
 * Returns either "white" or "black" for the best contrast
 * against the given background color.
 * @param hexColor - The background color in hex format (e.g. "#ff0000" or "ff0000")
 */
function getContrastColor(hexColor, darkColor = "black", lightColor = "white") {
    // Remove leading "#" if present
    const color = hexColor.replace(/^#/, "");
    // Parse R, G, B values
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    // Calculate the relative luminance (per W3C formula)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // Return white for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? darkColor : lightColor;
}

function getFieldValidation(configuration, fieldName, purpose) {
    return configuration?.fieldsDefinition
        ?.filter((f) => f.purpose === purpose)
        ?.find((f) => f.fieldName === fieldName && f.enabled === true)?.isMandatory;
}

const STRUCTURED_SAMPLE_VALUE_TRANSLATIONS = {
    en: {},
    de: {
        "On Track": "Im Plan",
        "At Risk": "Gefährdet",
        Critical: "Kritisch",
        "Not Started": "Nicht begonnen",
        "In Progress": "In Bearbeitung",
        Completed: "Abgeschlossen",
        "Very Low": "Sehr Niedrig",
        Low: "Niedrig",
        Medium: "Mittel",
        High: "Hoch",
        internal: "intern",
        external: "extern",
        forensic: "forensisch",
        compliance: "Compliance",
        operational: "operativ",
    },
};
const localizeStructuredSampleValue = (value, language) => value == null
    ? value
    : ((STRUCTURED_SAMPLE_VALUE_TRANSLATIONS[language] ??
        STRUCTURED_SAMPLE_VALUE_TRANSLATIONS.en)[value] ?? value);

dayjs.extend(utc);
const REPORTING_CYCLE_DATE_FORMAT = "YYYY-MM-DD";
function getReportingCycleDate(value) {
    return (value ? dayjs.utc(value) : dayjs.utc()).startOf("day");
}
function formatReportingCycleDate(value) {
    return getReportingCycleDate(value).format(REPORTING_CYCLE_DATE_FORMAT);
}

function countCompletedItemsPerCategory(projects, configuration) {
    const categoryCounts = {};
    if (!configuration.degreeOfCompletionDefinition || !projects?.length) {
        return [];
    }
    const degreeOfCompletionDefinition = configuration.degreeOfCompletionDefinition.find((completion) => completion.order === 2);
    const flattedReportingItemsWithCategory = projects
        .flatMap((p) => p.reportingItems?.map((i) => ({ ...i, category: p.category })))
        .filter((item) => getDropdownOptionStoredValue(findDropdownOption(configuration.degreeOfCompletionDefinition, item?.degreeOfCompletion)) === getDropdownOptionStoredValue(degreeOfCompletionDefinition));
    const existingCategories = configuration.categoryDefinition ?? [];
    const existingCategoryKeys = existingCategories
        .map((category) => getDropdownOptionStoredValue(category))
        .filter((category) => Boolean(category));
    for (const entry of flattedReportingItemsWithCategory?.map((entry) => entry?.category) ?? []) {
        const categoryKey = getDropdownOptionStoredValue(findDropdownOption(configuration.categoryDefinition, entry));
        if (!categoryKey || !existingCategoryKeys.includes(categoryKey)) {
            continue;
        }
        if (Object.keys(categoryCounts).includes(categoryKey)) {
            categoryCounts[categoryKey]++;
        }
        else {
            categoryCounts[categoryKey] = 1;
        }
    }
    const result = existingCategories.map((category) => ({
        label: category.name ?? "",
        value: categoryCounts[getDropdownOptionStoredValue(category) ?? category.name ?? ""] ?? 0,
        color: category.color,
    }));
    return result;
}

const countCompletedProjectsOverReportingCycle = (projects, configuration) => {
    if (!projects ||
        projects.length === 0 ||
        !configuration?.degreeOfCompletionDefinition ||
        !configuration?.reportingCycle?.startReportingDate ||
        !configuration?.reportingCycle?.endReportingDate ||
        !configuration?.reportingCycle?.frequency ||
        !configuration?.reportingCycle?.unit) {
        return {
            SIX_CYCLES: [],
            TWELVE_CYCLES: [],
            TWENTY_FOUR_CYCLES: [],
            ALL_CYCLES: [],
        };
    }
    const { frequency, unit } = configuration.reportingCycle;
    let startReportingDate = dayjs(configuration.reportingCycle.startReportingDate);
    const endReportingDate = dayjs(configuration.reportingCycle.endReportingDate);
    const endDate = endReportingDate.isBefore(dayjs())
        ? endReportingDate
        : dayjs();
    const completedItemsOverReportingCycle = [];
    while (startReportingDate.isBefore(endDate)) {
        const endReportingCycleDate = startReportingDate.add(frequency, unit);
        const completedItems = projects.filter((item) => {
            if (!item._calculated?.completionDate)
                return false;
            const completionDate = dayjs(item._calculated?.completionDate);
            if (!completionDate.isValid())
                return false;
            return (completionDate.isAfter(startReportingDate) &&
                !completionDate.isAfter(endReportingCycleDate));
        }).length;
        startReportingDate = startReportingDate.add(frequency, unit);
        completedItemsOverReportingCycle.push({
            category: dayjs(startReportingDate).format("YYYY-MM"),
            count: completedItems,
        });
    }
    return {
        SIX_CYCLES: completedItemsOverReportingCycle.slice(-6),
        TWELVE_CYCLES: completedItemsOverReportingCycle.slice(-12),
        TWENTY_FOUR_CYCLES: completedItemsOverReportingCycle.slice(-24),
        ALL_CYCLES: completedItemsOverReportingCycle,
    };
};

function countCompletedProjectsPerCategory(projects, configuration) {
    const categoryCounts = {};
    if (!configuration.degreeOfCompletionDefinition || !projects?.length) {
        return [];
    }
    const completedProjects = projects.filter((project) => project?._calculated?.completionDate);
    const existingCategories = configuration.categoryDefinition ?? [];
    const existingCategoryKeys = existingCategories
        .map((category) => getDropdownOptionStoredValue(category))
        .filter((category) => Boolean(category));
    for (const entry of completedProjects?.map((entry) => entry?.category) ??
        []) {
        const categoryKey = getDropdownOptionStoredValue(findDropdownOption(configuration.categoryDefinition, entry));
        if (!categoryKey || !existingCategoryKeys.includes(categoryKey)) {
            continue;
        }
        if (Object.keys(categoryCounts).includes(categoryKey)) {
            categoryCounts[categoryKey]++;
        }
        else {
            categoryCounts[categoryKey] = 1;
        }
    }
    const result = existingCategories.map((category) => ({
        label: category.name ?? "",
        value: categoryCounts[getDropdownOptionStoredValue(category) ?? category.name ?? ""] ?? 0,
        color: category.color,
    }));
    return result;
}

function countCompletionDegreesOfProjects(projects) {
    const completionDegreeCounts = {};
    for (const entry of projects?.map((entry) => entry._calculated?.overallDegreeOfCompletion?.name) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(completionDegreeCounts).includes(entry)) {
            completionDegreeCounts[entry]++;
        }
        else {
            completionDegreeCounts[entry] = 1;
        }
    }
    return { completionDegreeCounts };
}

function countSemaphoreStatusesOfProjects(projects) {
    const statusCounts = {};
    for (const entry of projects?.map((entry) => entry._calculated?.overallSemaphoreStatus?.name) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(statusCounts).includes(entry)) {
            statusCounts[entry]++;
        }
        else {
            statusCounts[entry] = 1;
        }
    }
    return { statusCounts };
}

function countStatusesOfProjects(projects) {
    const statusCounts = {};
    for (const entry of projects?.map((entry) => entry._calculated?.overallStatus?.name) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(statusCounts).includes(entry)) {
            statusCounts[entry]++;
        }
        else {
            statusCounts[entry] = 1;
        }
    }
    return { statusCounts };
}

// Translation object for all field labels
const translations = {
    en: {
        ID: "ID",
        Name: "Name",
        Owner: "Owner",
        Description: "Description",
        "Start date": "Start date",
        "End date": "End date",
        Budget: "Approved Budget",
        Severity: "Severity",
        Status: "Status",
        "Degree of Completion": "Degree of Completion",
        Progress: "Progress",
        Comment: "Comment",
        "Management Summary": "Management Summary",
        Challenges: "Challenges",
        Achievements: "Achievements",
        "Next Steps": "Next Steps",
        "Spent Budget": "Spent Budget",
        "Custom ID": "Custom ID",
        Category: "Category",
        Maturity: "Maturity",
    },
    de: {
        ID: "ID",
        Name: "Name",
        Owner: "Eigentümer",
        Description: "Beschreibung",
        "Start date": "Startdatum",
        "End date": "Enddatum",
        Budget: "Genehmigtes Budget",
        Severity: "Schweregrad",
        Status: "Status",
        "Degree of Completion": "Abschlussgrad",
        Progress: "Fortschritt",
        Comment: "Kommentar",
        "Management Summary": "Management-Zusammenfassung",
        Challenges: "Herausforderungen",
        Achievements: "Erfolge",
        "Next Steps": "Nächste Schritte",
        "Spent Budget": "Ausgegebenes Budget",
        "Custom ID": "Benutzerdefinierte ID",
        Category: "Kategorie",
        Maturity: "Reifegrad",
    },
};
// Function to get translated label
const label = (key, language = "en") => {
    return (translations[language][key] || key);
};
const localizeDefinitionNames = (configuration, language) => {
    const translate = (value) => localizeStructuredSampleValue(value, language);
    return {
        ...configuration,
        statusDefinition: configuration.statusDefinition?.map((definition) => ({
            ...definition,
            name: translate(definition.name) ?? definition.name,
            placeholder: translate(definition.placeholder) ?? definition.placeholder,
        })),
        degreeOfCompletionDefinition: configuration.degreeOfCompletionDefinition?.map((definition) => ({
            ...definition,
            name: translate(definition.name) ?? definition.name,
            placeholder: translate(definition.placeholder) ?? definition.placeholder,
        })),
        categoryDefinition: configuration.categoryDefinition?.map((definition) => ({
            ...definition,
            name: translate(definition.name) ?? definition.name,
            placeholder: translate(definition.placeholder) ?? definition.placeholder,
        })),
        secondCategoryDefinition: configuration.secondCategoryDefinition?.map((definition) => ({
            ...definition,
            name: translate(definition.name) ?? definition.name,
            placeholder: translate(definition.placeholder) ?? definition.placeholder,
        })),
        semaphoreStatusDefinition: configuration.semaphoreStatusDefinition?.map((definition) => ({
            ...definition,
            placeholder: translate(definition.placeholder) ?? definition.placeholder,
            low: {
                ...definition.low,
                name: translate(definition.low.name) ?? definition.low.name,
            },
            medium: {
                ...definition.medium,
                name: translate(definition.medium.name) ?? definition.medium.name,
            },
            high: {
                ...definition.high,
                name: translate(definition.high.name) ?? definition.high.name,
            },
        })),
    };
};
// Function to get audit configuration with language support
const getAuditConfig = (language = "en") => localizeDefinitionNames({
    id: "",
    useCase: "audit",
    projectName: "Finding",
    itemName: "Measure",
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: false,
    statusReportingLevel: "overall",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    fieldsDefinition: [
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: false,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("Severity", language),
            fieldName: "category",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: true,
            fieldInConfiguration: "categoryDefinition",
        },
        {
            label: "Audit Type",
            fieldName: "secondCategory",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: false,
            fieldInConfiguration: "secondCategoryDefinition",
        },
        {
            label: "Audit Title",
            fieldName: "freeTextFieldOne",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Team",
            fieldName: "dropdownFieldOne",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
            fieldInConfiguration: "dropdownFieldOneDefinition",
        },
        {
            label: label("Status", language),
            fieldName: "status",
            enabled: true,
            isEditable: false,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "statusDefinition",
        },
        {
            label: label("Degree of Completion", language),
            fieldName: "completion",
            enabled: true,
            isEditable: false,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "degreeOfCompletionDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: true,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: true,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: false,
            isEditable: true,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: false,
        },
    ],
    dropdownFieldOneDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            color: "#ccc",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            color: "#ccc",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            color: "#ccc",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            color: "#ccc",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            color: "#ccc",
            order: 4,
        },
    ],
    degreeOfCompletionDefinition: [
        {
            name: "Not Started",
            color: "#D9D9D9",
            label: "Choose a label for progress not yet started",
            placeholder: "Not Started",
            order: 0,
        },
        {
            name: "In Progress",
            color: "#FFB74D",
            label: "Choose a label for in progress",
            placeholder: "In Progress",
            order: 1,
        },
        {
            name: "Completed",
            color: "#4CAF50",
            label: "Choose a label for completed",
            placeholder: "Completed",
            order: 2,
        },
    ],
    categoryDefinition: [
        {
            name: "Very Low",
            label: "Choose a label for very low severity",
            color: "#f4fc0cff",
            placeholder: "Very Low",
            order: 0,
        },
        {
            name: "Low",
            label: "Choose a label for low severity",
            color: "#ffecb3",
            placeholder: "Low",
            order: 1,
        },
        {
            name: "Medium",
            label: "Choose a label for medium severity",
            color: "#ffe082",
            placeholder: "Medium",
            order: 2,
        },
        {
            name: "High",
            label: "Choose a label for high severity",
            color: "#f4870aff",
            placeholder: "High",
            order: 3,
        },
        {
            name: "Critical",
            label: "Choose a label for critical severity",
            color: "#ef3726ff",
            placeholder: "Critical",
            order: 4,
        },
    ],
    secondCategoryDefinition: [
        {
            name: "internal",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "external",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "forensic",
            label: "Choose a label for forensic",
            placeholder: "",
            order: 2,
        },
        {
            name: "compliance",
            label: "Choose a label for compliance",
            placeholder: "",
            order: 3,
        },
        {
            name: "operational",
            label: "Choose a label for operational",
            placeholder: "",
            order: 4,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
}, language);
// Function to get PMO configuration with language support
const getPmoConfig = (language = "en") => localizeDefinitionNames({
    id: "",
    useCase: "pmo",
    projectName: "Project",
    itemName: "Deliverable",
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: true,
    statusReportingLevel: "category",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    secondCategoryDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            order: 4,
        },
    ],
    fieldsDefinition: [
        {
            label: label("Custom ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "category",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: false,
            fieldInConfiguration: "categoryDefinition",
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "dropdownFieldOne",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
            fieldInConfiguration: "dropdownFieldOneDefinition",
        },
        {
            label: label("Status", language),
            fieldName: "status",
            enabled: true,
            isEditable: false,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "semaphoreStatusDefinition",
        },
        {
            label: label("Degree of Completion", language),
            fieldName: "completion",
            enabled: true,
            isEditable: false,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "degreeOfCompletionDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: true,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: false,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: true,
        },
    ],
    degreeOfCompletionDefinition: [
        {
            name: "Not Started",
            color: "#D9D9D9",
            label: "Choose a label for progress not yet started",
            placeholder: "Not Started",
            order: 0,
        },
        {
            name: "In Progress",
            color: "#FFB74D",
            label: "Choose a label for in progress",
            placeholder: "In Progress",
            order: 1,
        },
        {
            name: "Completed",
            color: "#4CAF50",
            label: "Choose a label for completed",
            placeholder: "Completed",
            order: 2,
        },
    ],
    dropdownFieldOneDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            placeholder: "",
            color: "#ccc",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            placeholder: "",
            color: "#ccc",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            placeholder: "",
            color: "#ccc",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            placeholder: "",
            color: "#ccc",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            placeholder: "",
            color: "#ccc",
            order: 4,
        },
    ],
    categoryDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            color: "#f4fc0cff",
            placeholder: "Very Low",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            color: "#ffecb3",
            placeholder: "Low",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            color: "#ffe082",
            placeholder: "Medium",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            color: "#f4870aff",
            placeholder: "High",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            color: "#ef3726ff",
            placeholder: "Critical",
            order: 4,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
    semaphoreStatusDefinition: [
        {
            name: "Time",
            low: {
                name: "On Track",
                color: "green",
                order: 0,
            },
            medium: {
                name: "At Risk",
                color: "yellow",
                order: 1,
            },
            high: {
                name: "Critical",
                color: "red",
                order: 2,
            },
            label: "Choose a label for On‑Track items",
            placeholder: "Time",
            order: 0,
        },
        {
            name: "Budget",
            low: {
                name: "On Track",
                color: "green",
                order: 0,
            },
            medium: {
                name: "At Risk",
                color: "yellow",
                order: 1,
            },
            high: {
                name: "Critical",
                color: "red",
                order: 2,
            },
            label: "Choose a label for At‑Risk items",
            placeholder: "Budget",
            order: 1,
        },
        {
            name: "Scope",
            low: {
                name: "On Track",
                color: "green",
                order: 0,
            },
            medium: {
                name: "At Risk",
                color: "yellow",
                order: 1,
            },
            high: {
                name: "Critical",
                color: "red",
                order: 2,
            },
            label: "Choose a label for Critical items",
            placeholder: "Scope",
            order: 2,
        },
    ],
}, language);
// Function to get rollout configuration with language support
const getRolloutConfig = (language = "en") => localizeDefinitionNames({
    id: "",
    useCase: "rollout",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: true,
    statusReportingLevel: "overall",
    projectName: "Project",
    itemName: "Activity",
    secondCategoryDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            order: 4,
        },
    ],
    fieldsDefinition: [
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "category",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: false,
            fieldInConfiguration: "categoryDefinition",
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "dropdownFieldOne",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
            fieldInConfiguration: "dropdownFieldOneDefinition",
        },
        {
            label: label("Status", language),
            fieldName: "status",
            enabled: true,
            isEditable: false,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "statusDefinition",
        },
        {
            label: label("Degree of Completion", language),
            fieldName: "completion",
            enabled: true,
            isEditable: false,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "degreeOfCompletionDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: true,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: true,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: true,
        },
    ],
    dropdownFieldOneDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            placeholder: "",
            color: "#ccc",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            placeholder: "",
            color: "#ccc",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            placeholder: "",
            color: "#ccc",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            placeholder: "",
            color: "#ccc",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            placeholder: "",
            color: "#ccc",
            order: 4,
        },
    ],
    categoryDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            color: "#f4fc0cff",
            placeholder: "Very Low",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            color: "#ffecb3",
            placeholder: "Low",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            color: "#ffe082",
            placeholder: "Medium",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            color: "#f4870aff",
            placeholder: "High",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            color: "#ef3726ff",
            placeholder: "Critical",
            order: 4,
        },
    ],
    degreeOfCompletionDefinition: [
        {
            name: "Not Started",
            color: "#D9D9D9",
            label: "Choose a label for progress not yet started",
            placeholder: "Not Started",
            order: 0,
        },
        {
            name: "In Progress",
            color: "#FFB74D",
            label: "Choose a label for in progress",
            placeholder: "In Progress",
            order: 1,
        },
        {
            name: "Completed",
            color: "#4CAF50",
            label: "Choose a label for completed",
            placeholder: "Completed",
            order: 2,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
}, language);
const getAssessmentConfig = (language = "en") => localizeDefinitionNames({
    id: "",
    useCase: "assessment",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: true,
    statusReportingLevel: "overall",
    projectName: "Company",
    itemName: "Question",
    secondCategoryDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            order: 4,
        },
    ],
    fieldsDefinition: [
        {
            label: label("ID", language),
            fieldName: "id",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: false,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: false,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: false,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: false,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: false,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: "Answer",
            fieldName: "answer",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "answerDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: false,
            isEditable: false,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: true,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: false,
            isEditable: false,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: false,
        },
    ],
    answerDefinition: [
        {
            name: "option1",
            label: "Choose a label for option 1",
            placeholder: "",
            order: 0,
        },
        {
            name: "option2",
            label: "Choose a label for option 2",
            placeholder: "",
            order: 1,
        },
        {
            name: "option3",
            label: "Choose a label for option 3",
            placeholder: "",
            order: 2,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
}, language);
// Backward compatibility exports (default to English)
const AUDIT_CONFIG = getAuditConfig("en");
const PMO_CONFIG = getPmoConfig("en");
const ROLLOUT_CONFIG = getRolloutConfig("en");
const ASSESSMENT_CONFIG = getAssessmentConfig("en");
const DEFAULT_CONFIG = getAuditConfig("en");
const getDefaultConfig = getAuditConfig;
// Function to get maturity configuration with language support (copy of audit)
const getMaturityConfig = (language = "en") => {
    const base = getAuditConfig(language);
    const maturityField = {
        label: label("Maturity", language),
        fieldName: "maturity",
        enabled: true,
        isEditable: false,
        type: "Dropdown",
        purpose: "itemBasic",
        isMandatory: true,
        fieldInConfiguration: "maturityConfiguration",
    };
    const dimensionField = {
        label: "Dimension",
        fieldName: "dimension",
        enabled: true,
        isEditable: true,
        type: "Dropdown",
        purpose: "itemBasic",
        isMandatory: false,
        fieldInConfiguration: "dimensionDefinition",
    };
    return {
        ...base,
        useCase: "maturity",
        fieldsDefinition: (base.fieldsDefinition ?? [])
            .filter((field) => field.fieldName !== "secondCategory")
            .filter((field) => field.fieldName !== "freeTextFieldOne")
            .flatMap((field) => {
            if (field.fieldName === "name" && field.purpose === "itemBasic") {
                return [field, maturityField, dimensionField];
            }
            if (field.purpose === "itemReporting" && field.fieldName === "status") {
                return [
                    {
                        ...field,
                        enabled: false,
                        isEditable: true,
                        isMandatory: false,
                    },
                ];
            }
            if (field.purpose === "itemReporting" &&
                field.fieldName === "progress") {
                return [
                    {
                        ...field,
                        enabled: false,
                        isEditable: true,
                        isMandatory: false,
                    },
                ];
            }
            return [field];
        }),
        dimensionDefinition: [
            {
                name: "internal",
                label: "Choose a label for internal",
                placeholder: "Internal",
                color: "#90CAF9",
                order: 0,
            },
            {
                name: "external",
                label: "Choose a label for external",
                placeholder: "External",
                color: "#80CBC4",
                order: 1,
            },
            {
                name: "forensic",
                label: "Choose a label for forensic",
                placeholder: "Forensic",
                color: "#FFCC80",
                order: 2,
            },
            {
                name: "compliance",
                label: "Choose a label for compliance",
                placeholder: "Compliance",
                color: "#EF9A9A",
                order: 3,
            },
            {
                name: "operational",
                label: "Choose a label for operational",
                placeholder: "Operational",
                color: "#CE93D8",
                order: 4,
            },
        ],
        maturityConfiguration: [
            {
                name: "Initial",
                label: "Choose a label for initial maturity",
                placeholder: "Initial",
                color: "#D9D9D9",
                order: 0,
            },
            {
                name: "Repeatable",
                label: "Choose a label for repeatable maturity",
                placeholder: "Repeatable",
                color: "#B0BEC5",
                order: 1,
            },
            {
                name: "Defined",
                label: "Choose a label for defined maturity",
                placeholder: "Defined",
                color: "#81C784",
                order: 2,
            },
            {
                name: "Managed",
                label: "Choose a label for managed maturity",
                placeholder: "Managed",
                color: "#4DB6AC",
                order: 3,
            },
            {
                name: "Optimizing",
                label: "Choose a label for optimizing maturity",
                placeholder: "Optimizing",
                color: "#43A047",
                order: 4,
            },
        ],
    };
};
const MATURITY_CONFIG = getMaturityConfig("en");

const AUDIT_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "statusComponent" },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfTrackingItemsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 3,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "progressComponent" },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfProjectsDonutChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "statusOfItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completionOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionOfTrackingItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsCategoryComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsPerCategoryComponent",
                            },
                        ],
                    },
                    {
                        id: 6,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsLineChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    overview: {
        displayTabs: false,
        canEdit: true,
        tabs: [
            {
                id: 0,
                title: "Project Overview",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "projectsTableComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfDueItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "reportingProgressComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "auditComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "descriptionComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "managementSummaryComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusDonutComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionDonutComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 1,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Measures",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "countStatusComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "countCompletionComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};
const PMO_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "progressComponent" },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfProjectsDonutChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "statusOfItemsDonutChartComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            { columnIndex: 0, componentId: "programDescriptionComponent" },
                            { columnIndex: 1, componentId: "managementSummaryComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    overview: {
        displayTabs: false,
        canEdit: true,
        tabs: [
            {
                id: 0,
                title: "Project Overview",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "projectsTableComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfDueItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "reportingProgressComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfItemsDonutChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "descriptionComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "managementSummaryComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "challengesDescriptionComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Deliverables",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "reportingProgressComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};
const ROLLOUT_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "statusComponent" },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfTrackingItemsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "progressComponent" },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfProjectsDonutChartComponent",
                            },
                            { columnIndex: 1, componentId: "statusOfItemsComponent" },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completionOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionOfTrackingItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsLineChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    overview: {
        displayTabs: false,
        canEdit: true,
        tabs: [
            {
                id: 0,
                title: "Project Overview",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "projectsTableComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfDueItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "reportingProgressComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusDonutComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionDonutComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedItemsOverTimeComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "managementSummaryComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Activities",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "countStatusComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "countCompletionComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};
// Maturity view config — the status and progress fields are disabled by
// default for this use case (see `getDefaultConfigurationForUseCase`), so the
// layout swaps every status/progress card the audit default has for the
// maturity and dimension cards that are actually visible for this use case.
const MATURITY_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "averageProjectMaturityComponent",
                            },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfTrackingItemsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 3,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "oeMaturityLevelComponent" },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completionPerMaturityLineComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "maturityOfItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completionOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionOfTrackingItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsCategoryComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsPerCategoryComponent",
                            },
                        ],
                    },
                    {
                        id: 6,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsLineChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    overview: AUDIT_VIEW_CONFIG.overview,
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectMaturityBadgeCard",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfDueItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "reportMaturityLevelComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "auditComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "descriptionComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "managementSummaryComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "dimensionTableComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionDonutComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 1,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Activities",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "categoryCard",
                            },
                            {
                                columnIndex: 3,
                                componentId: "countCompletionComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};

const VIEW_NAMES = [
    "reporting-structure",
    "reporting-period",
    "manage-projects-data-model",
    "manage-items-data-model",
    "manage-reporting-data",
    "manage-main-entities",
    "manage-subentities",
    "manage-users",
    "basic-notifications",
    "reporting-use-case",
];

function calculateAverageProgress(subentities) {
    let averageProgress = 0;
    if (!subentities || subentities.length === 0) {
        return { averageProgress: 0 };
    }
    for (const entry of subentities?.map((entry) => entry.progress) ?? []) {
        averageProgress += entry ?? 0;
    }
    averageProgress = Number.parseFloat((averageProgress / subentities?.length).toFixed(2));
    return { averageProgress };
}

/**
 * Maps an itemReporting fieldName to the corresponding property on ReportingItem.
 * Attachment and project-level fields (managementSummary) are excluded because
 * they cannot be checked with a simple scalar comparison.
 */
const FIELD_NAME_TO_ITEM_KEY = {
    status: "status",
    completion: "degreeOfCompletion",
    progress: "progress",
    comment: "comment",
    answer: "answer",
    spentBudget: "spentBudget",
};
/** Checked when no enabled itemReporting fields map to a ReportingItem key. */
const FALLBACK_KEYS = [
    "status",
    "degreeOfCompletion",
    "progress",
    "comment",
    "answer",
];
function isFieldFilled(item, key) {
    const value = item[key];
    if (value === null || value === undefined)
        return false;
    if (typeof value === "string")
        return value.trim() !== "";
    // For numbers, only count as filled when the user explicitly set a value.
    // TypeORM columns default to 0; an explicit value is > 0 for progress-style
    // fields.  spentBudget can legitimately be 0, but 0 budget unset is
    // indistinguishable, so we accept 0 as "not filled" to avoid false positives.
    if (typeof value === "number")
        return value !== 0;
    return Boolean(value);
}
/**
 * Returns a completion status per dimension based on how many of each
 * dimension's activities have all *mandatory* itemReporting fields filled.
 *
 * - "not-started" : no activity has any mandatory reporting field filled
 * - "completed"   : every activity has every mandatory reporting field filled
 * - "in-progress" : anything in between
 */
function calculateDimensionFormCompletion(reportingItems, configuration) {
    const enabledReportingFieldKeys = (configuration?.fieldsDefinition ?? [])
        .filter((f) => f.purpose === "itemReporting" && f.enabled && f.isMandatory)
        .map((f) => FIELD_NAME_TO_ITEM_KEY[f.fieldName])
        .filter((key) => key !== undefined);
    const result = {};
    const dimensions = [
        ...new Set(reportingItems.map((item) => item.basicItem?.dimension || "No Dimension")),
    ];
    for (const dimension of dimensions) {
        const items = reportingItems.filter((item) => (item.basicItem?.dimension || "No Dimension") === dimension);
        if (items.length === 0) {
            result[dimension] = "not-started";
            continue;
        }
        // Fall back to a hardcoded set when the configuration yields no mapped keys
        // (e.g. configuration.fieldsDefinition is undefined or all relevant fields
        // are disabled in the persisted config).
        const keysToCheck = enabledReportingFieldKeys.length > 0
            ? enabledReportingFieldKeys
            : FALLBACK_KEYS;
        let anyFilled = false;
        let allFilled = true;
        for (const item of items) {
            const itemFullyFilled = keysToCheck.every((key) => isFieldFilled(item, key));
            const itemAnyFilled = keysToCheck.some((key) => isFieldFilled(item, key));
            if (itemAnyFilled)
                anyFilled = true;
            if (!itemFullyFilled)
                allFilled = false;
        }
        if (allFilled) {
            result[dimension] = "completed";
        }
        else if (anyFilled) {
            result[dimension] = "in-progress";
        }
        else {
            result[dimension] = "not-started";
        }
    }
    return result;
}

function calculateLastUpdateDateForProject(subentities, projectReportLastUpdatedAt, projectReportLastUpdatedBy) {
    const sorted = subentities
        .filter((entity) => entity.lastUpdatedAt)
        .sort((a, b) => new Date(b.lastUpdatedAt ?? new Date().toISOString()).getTime() -
        new Date(a.lastUpdatedAt ?? new Date().toISOString()).getTime());
    const latestItemAt = sorted[0]?.lastUpdatedAt ?? null;
    const latestItemBy = sorted[0]?.lastUpdatedBy;
    // If the project was updated via the report path and that timestamp is more
    // recent than any individual item update, surface it as the project update.
    if (projectReportLastUpdatedAt &&
        (!latestItemAt ||
            new Date(projectReportLastUpdatedAt).getTime() >
                new Date(latestItemAt).getTime())) {
        return {
            lastUpdatedAt: projectReportLastUpdatedAt,
            lastUpdatedBy: projectReportLastUpdatedBy,
        };
    }
    return {
        lastUpdatedAt: latestItemAt,
        lastUpdatedBy: latestItemBy,
    };
}

function toGaugePercentage(value, maxValue) {
    if (maxValue <= 1) {
        const clampedValue = Math.max(0, Math.min(value, 1));
        return clampedValue * 100;
    }
    const clampedValue = Math.max(0, Math.min(value, maxValue));
    return (clampedValue / maxValue) * 100;
}
function getOrderedMaturityLevels(configuration) {
    return (configuration?.maturityConfiguration ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((entry) => ({
        name: entry.name ?? "",
        color: entry.color,
    }))
        .filter((entry) => Boolean(entry.name));
}
function getCompletedDegreeName(configuration) {
    return ((configuration?.degreeOfCompletionDefinition ?? []).find((entry) => entry.order === 2)?.name ?? "");
}
function isItemCompleted(item, completedDegreeName) {
    return item.degreeOfCompletion === completedDegreeName;
}
/**
 * Returns the number of achieved maturity levels and the color corresponding
 * to that numeric level for a set of items belonging to a single dimension.
 * Empty levels are skipped; counting stops at the first level that has items
 * but not all are completed.  If the dimension has NO items the value is 0.
 * Color is determined by the value's position in the maturity scale so that
 * equal values always produce the same color.
 */
function getTopicMultiLevelResult(topicItems, maturityLevels, completedDegreeName) {
    const defaultColor = maturityLevels[0]?.color;
    if (!topicItems.length || !maturityLevels.length) {
        return { value: 0, color: defaultColor };
    }
    let achievedLevels = 0;
    for (const level of maturityLevels) {
        const levelItems = topicItems.filter((item) => (item.basicItem?.maturity ?? "") === level.name);
        // No items at this level — skip, neither count nor block
        if (!levelItems.length) {
            continue;
        }
        // Items exist but not all completed — stop here
        if (!levelItems.every((item) => isItemCompleted(item, completedDegreeName))) {
            break;
        }
        achievedLevels++;
    }
    // Color is based on the numeric value's position so equal values share the same color
    const colorIndex = achievedLevels > 0
        ? Math.min(achievedLevels - 1, maturityLevels.length - 1)
        : 0;
    return { value: achievedLevels, color: maturityLevels[colorIndex]?.color };
}
function getOrderedTopics(items, configuration) {
    const topicsFromItems = Array.from(new Set(items
        .map((item) => item.basicItem?.dimension)
        .filter((name) => Boolean(name))));
    const configuredTopics = (configuration?.dimensionDefinition ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((entry) => entry.name)
        .filter((name) => Boolean(name));
    if (!configuredTopics.length && !topicsFromItems.length) {
        return [];
    }
    const configuredTopicSet = new Set(configuredTopics);
    return [
        ...configuredTopics,
        ...topicsFromItems.filter((topic) => !configuredTopicSet.has(topic)),
    ].filter((topic, index, source) => source.indexOf(topic) === index);
}
function getCurrentMaturityIndexForItems(items, configuration) {
    const maturityLevels = getOrderedMaturityLevels(configuration);
    const completedDegreeName = getCompletedDegreeName(configuration);
    const topics = getOrderedTopics(items, configuration);
    if (topics.length > 0) {
        const topicValues = topics.map((topicName) => {
            const topicItems = items.filter((item) => (item.basicItem?.dimension ?? "") === topicName);
            return getTopicMultiLevelResult(topicItems, maturityLevels, completedDegreeName).value;
        });
        if (!topicValues.length) {
            return 0;
        }
        return (topicValues.reduce((sum, value) => sum + value, 0) / topicValues.length);
    }
    // Fallback: no dimension on items — treat all items as one group
    return getTopicMultiLevelResult(items, maturityLevels, completedDegreeName)
        .value;
}
function getCurrentMaturityLabelForItems(items, configuration) {
    const currentValue = getCurrentMaturityIndexForItems(items, configuration);
    if (Number.isInteger(currentValue)) {
        return String(currentValue);
    }
    return currentValue.toFixed(2);
}
function calculateMaturityGauge({ configuration, items, }) {
    const maturityLevels = getOrderedMaturityLevels(configuration);
    const completedDegreeName = getCompletedDegreeName(configuration);
    const maxValue = maturityLevels.length;
    const orderedTopics = getOrderedTopics(items, configuration);
    const scale = Array.from({ length: maxValue + 1 }, (_, i) => i);
    const dimensions = orderedTopics.map((topicName) => {
        const topicItems = items.filter((item) => (item.basicItem?.dimension ?? "") === topicName);
        const { value, color } = getTopicMultiLevelResult(topicItems, maturityLevels, completedDegreeName);
        return {
            name: topicName,
            value,
            percentage: toGaugePercentage(value, maxValue),
            color,
        };
    });
    const averageValue = dimensions.length
        ? dimensions.reduce((sum, dimension) => sum + dimension.value, 0) /
            dimensions.length
        : getTopicMultiLevelResult(items, maturityLevels, completedDegreeName)
            .value;
    const averageColor = dimensions.length
        ? getTopicMultiLevelResult(items.filter((item) => orderedTopics.includes(item.basicItem?.dimension ?? "")), maturityLevels, completedDegreeName).color
        : getTopicMultiLevelResult(items, maturityLevels, completedDegreeName)
            .color;
    return {
        scale,
        averageValue,
        averagePercentage: toGaugePercentage(averageValue, maxValue),
        averageColor,
        dimensions,
    };
}

function calculateNumberOfOverdueSubEntities(subentities) {
    return {
        numberOfOverdueSubEntities: subentities?.filter((entity) => entity.basicItem?.endDate &&
            dayjs(entity.basicItem?.endDate).isBefore(dayjs())).length ?? 0,
    };
}

function calculateOverallDegreeOfCompletion(degreeOfCompletionCounts, completionDefinition, totalItemCount) {
    const hasCount = (definition) => (degreeOfCompletionCounts[getDropdownOptionStoredValue(definition) ?? ""] ??
        0) > 0 || (degreeOfCompletionCounts[definition.name ?? ""] ?? 0) > 0;
    const sortedDefinitions = (completionDefinition ?? [])
        .slice()
        .sort((a, b) => a.order - b.order);
    // Find all statuses with count > 0
    const presentStatuses = sortedDefinitions.filter((def) => hasCount(def));
    // Items whose degree of completion was never reported are excluded from
    // `degreeOfCompletionCounts` (see calculateOverallDegreeOfCompletionCount),
    // so their absence must not be mistaken for "everyone reported a good
    // status". If any item wasn't counted, the project is behind whatever was
    // reported, so it's treated as being at the lowest defined order.
    const countedItemCount = Object.values(degreeOfCompletionCounts).reduce((sum, count) => sum + count, 0);
    const hasUnreportedItems = totalItemCount !== undefined && countedItemCount < totalItemCount;
    // Pick the lowest order present status
    const overallDegreeOfCompletion = (hasUnreportedItems
        ? sortedDefinitions[0]
        : undefined) ??
        presentStatuses[0] ?? { order: 0 };
    return { overallDegreeOfCompletion };
}

function calculateOverallDegreeOfCompletionCount(subentities) {
    const degreeOfCompletionCounts = {};
    for (const entry of subentities?.map((entry) => entry.degreeOfCompletion) ??
        []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(degreeOfCompletionCounts).includes(entry)) {
            degreeOfCompletionCounts[entry]++;
        }
        else {
            degreeOfCompletionCounts[entry] = 1;
        }
    }
    return { degreeOfCompletionCounts };
}

function calculateOverallSeverityCounts(subentities) {
    const severityCounts = {};
    for (const entry of subentities?.map((entry) => entry.severity) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(severityCounts).includes(entry)) {
            severityCounts[entry]++;
        }
        else {
            severityCounts[entry] = 1;
        }
    }
    return { severityCounts };
}

function calculateOverallStatus(statusCounts, statusDefinition) {
    let overallStatus = { order: 0 };
    Object.entries(statusCounts).forEach(([statusName, count]) => {
        if (count <= 0) {
            return;
        }
        const status = findDropdownOption(statusDefinition, statusName);
        if (status &&
            overallStatus.order <= status?.order &&
            getDropdownOptionStoredValue(status) != null) {
            overallStatus = status;
        }
    });
    return { overallStatus };
}

function calculateOverallStatusCount(subentities) {
    const statusCounts = {};
    for (const entry of subentities?.map((entry) => entry.status) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(statusCounts).includes(entry)) {
            statusCounts[entry]++;
        }
        else {
            statusCounts[entry] = 1;
        }
    }
    return { statusCounts };
}

function calculateProjectBudgetDetails(project) {
    const approvedBudget = project?.approvedBudget ?? 0;
    const spentBudget = project.spentBudget ?? 0;
    const percentageOfBudgetSpent = approvedBudget === 0 ? 0 : Math.round((spentBudget / approvedBudget) * 100);
    return percentageOfBudgetSpent;
}

/**
 * Scans a list of reporting items and determines the worst semaphore level
 * for semaphoreStatus1, semaphoreStatus2 and semaphoreStatus3 separately.
 * Priority: high > medium > low. Returns an object with properties status1,status2,status3
 * containing the matching level object or undefined.
 */
function calculateSemaphoreStatus(reportingItems, semaphoreStatusDefinition) {
    const def = Array.isArray(semaphoreStatusDefinition)
        ? semaphoreStatusDefinition[0]
        : undefined;
    if (!def || !reportingItems || reportingItems.length === 0)
        return { status1: undefined, status2: undefined, status3: undefined };
    const values1 = reportingItems
        .map((ri) => ri.semaphoreStatus1)
        .filter(Boolean);
    const values2 = reportingItems
        .map((ri) => ri.semaphoreStatus2)
        .filter(Boolean);
    const values3 = reportingItems
        .map((ri) => ri.semaphoreStatus3)
        .filter(Boolean);
    const matchesAny = (values, level) => level != null &&
        values.some((value) => value === level.id || value === level.name);
    const status1 = matchesAny(values1, def.high)
        ? def.high
        : matchesAny(values1, def.medium)
            ? def.medium
            : matchesAny(values1, def.low)
                ? def.low
                : undefined;
    const status2 = matchesAny(values2, def.high)
        ? def.high
        : matchesAny(values2, def.medium)
            ? def.medium
            : matchesAny(values2, def.low)
                ? def.low
                : undefined;
    const status3 = matchesAny(values3, def.high)
        ? def.high
        : matchesAny(values3, def.medium)
            ? def.medium
            : matchesAny(values3, def.low)
                ? def.low
                : undefined;
    // Determine overall worst: high > medium > low
    const overall = status1 === def.high || status2 === def.high || status3 === def.high
        ? def.high
        : status1 === def.medium ||
            status2 === def.medium ||
            status3 === def.medium
            ? def.medium
            : status1 === def.low || status2 === def.low || status3 === def.low
                ? def.low
                : undefined;
    return { status1, status2, status3, overall };
}

/**
 * Accumulates the count of completed items over each reporting cycle timeframe.
 *
 * Given an object mapping each timeframe (from TIMEFRAMES) to an array of { category, count },
 * this function returns a new object with the same structure, but where each array's counts are
 * accumulated sequentially. Each element's count is the sum of its own count and all previous counts
 * in that array.
 *
 * Example:
 * Input: {
 *   MONTH: [
 *     { category: '2024-01-01', count: 1 },
 *     { category: '2024-02-01', count: 1 }
 *   ]
 * }
 * Output: {
 *   MONTH: [
 *     { category: '2024-01-01', count: 1 },
 *     { category: '2024-02-01', count: 2 }
 *   ]
 * }
 *
 * @param itemsByTimeframe - An object mapping each timeframe to an array of category/count pairs.
 * @returns An object with the same keys, where each array's counts are accumulated.
 */
function countAccumulatedItemsOverReportingCycle(itemsByTimeframe) {
    const allItems = itemsByTimeframe[TIMEFRAMES.ALL_CYCLES] || [];
    let runningTotal = 0;
    const result = allItems.map((item) => {
        runningTotal += item.count;
        return { category: item.category, count: runningTotal };
    });
    return {
        SIX_CYCLES: result.slice(-6),
        TWELVE_CYCLES: result.slice(-12),
        TWENTY_FOUR_CYCLES: result.slice(-24),
        ALL_CYCLES: result,
    };
}

function countCompletedReportingItems(reportingItems, configuration) {
    if (!reportingItems ||
        reportingItems.length === 0 ||
        !configuration?.degreeOfCompletionDefinition) {
        return 0;
    }
    const degreeOfCompletionDefinition = configuration.degreeOfCompletionDefinition.find((completion) => completion.order === 2);
    if (!degreeOfCompletionDefinition) {
        return 0;
    }
    return reportingItems.filter((subentity) => getDropdownOptionStoredValue(findDropdownOption(configuration.degreeOfCompletionDefinition, subentity.degreeOfCompletion)) === getDropdownOptionStoredValue(degreeOfCompletionDefinition)).length;
}

function countCompletedItemsPerCategoryLevel(reportingItems, configuration) {
    const categoryCounts = {};
    if (!configuration.dropdownFieldOneDefinition ||
        !configuration.degreeOfCompletionDefinition ||
        !reportingItems?.length) {
        return [];
    }
    const degreeOfCompletionDefinition = configuration.degreeOfCompletionDefinition.find((completion) => completion.order === 2);
    const flattedReportingItemsWithCategory = reportingItems
        ?.map((i) => ({
        ...i,
        dropdownFieldOne: i.basicItem?.dropdownFieldOne,
    }))
        .filter((item) => getDropdownOptionStoredValue(findDropdownOption(configuration.degreeOfCompletionDefinition, item?.degreeOfCompletion)) === getDropdownOptionStoredValue(degreeOfCompletionDefinition));
    const existingCategories = configuration.dropdownFieldOneDefinition ?? [];
    const existingCategoryKeys = existingCategories
        .map((category) => getDropdownOptionStoredValue(category))
        .filter((category) => Boolean(category));
    for (const entry of flattedReportingItemsWithCategory?.map((entry) => entry?.dropdownFieldOne) ?? []) {
        const categoryKey = getDropdownOptionStoredValue(findDropdownOption(configuration.dropdownFieldOneDefinition, entry));
        if (!categoryKey || !existingCategoryKeys.includes(categoryKey)) {
            continue;
        }
        if (Object.keys(categoryCounts).includes(categoryKey)) {
            categoryCounts[categoryKey]++;
        }
        else {
            categoryCounts[categoryKey] = 1;
        }
    }
    const result = existingCategories.map((category) => ({
        label: category.name ?? "",
        value: categoryCounts[getDropdownOptionStoredValue(category) ?? category.name ?? ""] ?? 0,
        color: category.color,
    }));
    return result;
}

const countCompletedItemsOverReportingCycle = (reportingItems, configuration) => {
    if (!reportingItems ||
        reportingItems.length === 0 ||
        !configuration?.degreeOfCompletionDefinition ||
        !configuration?.reportingCycle?.startReportingDate ||
        !configuration?.reportingCycle?.endReportingDate ||
        !configuration?.reportingCycle?.frequency ||
        !configuration?.reportingCycle?.unit) {
        return {
            SIX_CYCLES: [],
            TWELVE_CYCLES: [],
            TWENTY_FOUR_CYCLES: [],
            ALL_CYCLES: [],
        };
    }
    const degreeOfCompletionDefinition = configuration.degreeOfCompletionDefinition.find((completion) => completion.order === 2);
    const { frequency, unit } = configuration.reportingCycle;
    let startReportingDate = dayjs(configuration.reportingCycle.startReportingDate);
    const endReportingDate = dayjs(configuration.reportingCycle.endReportingDate);
    const endDate = endReportingDate.isBefore(dayjs())
        ? endReportingDate
        : dayjs();
    const completedItemsOverReportingCycle = [];
    while (startReportingDate.isBefore(endDate)) {
        const endReportingCycleDate = startReportingDate.add(frequency, unit);
        const completedItems = reportingItems.filter((item) => {
            if (!item.completionDate)
                return false;
            const completionDate = dayjs(item.completionDate);
            if (!completionDate.isValid())
                return false;
            return (getDropdownOptionStoredValue(findDropdownOption(configuration.degreeOfCompletionDefinition, item.degreeOfCompletion)) === getDropdownOptionStoredValue(degreeOfCompletionDefinition) &&
                completionDate.isAfter(startReportingDate) &&
                !completionDate.isAfter(endReportingCycleDate));
        }).length;
        completedItemsOverReportingCycle.push({
            category: startReportingDate.format("YYYY-MM"),
            count: completedItems,
        });
        startReportingDate = startReportingDate.add(frequency, unit);
    }
    return {
        SIX_CYCLES: completedItemsOverReportingCycle.slice(-6),
        TWELVE_CYCLES: completedItemsOverReportingCycle.slice(-12),
        TWENTY_FOUR_CYCLES: completedItemsOverReportingCycle.slice(-24),
        ALL_CYCLES: completedItemsOverReportingCycle,
    };
};

function countOverdueProjects(projects) {
    return projects.filter((project) => project.endDate && dayjs(project.endDate).isBefore(dayjs(), "day")).length;
}

function countSemaphoreStatusesOfReportingItems(reportingItems) {
    const statusCounts = {};
    for (const statusName of reportingItems?.map((item) => item._calculated?.semaphoreStatus?.name) ?? []) {
        if (!statusName) {
            continue;
        }
        statusCounts[statusName] = (statusCounts[statusName] ?? 0) + 1;
    }
    return { statusCounts };
}

/**
 * Normalize an array of custom field values by referenceId.
 *
 * This function groups values that share the same customFieldDefinition.referenceId and
 * returns a single entry per referenceId according to the following rules:
 * - If one of the entries has a customFieldDefinition.id contained in preferredDefinitionIds,
 *   that entry is preferred.
 * - Otherwise, an entry that has a "meaningful" value (value != null and non-empty after trimming)
 *   is preferred over one without a meaningful value.
 * - If neither rule disambiguates, the first seen entry is kept.
 *
 * Values that do not contain a customFieldDefinition.referenceId are preserved in the
 * normalized output in their original order.
 *
 * Generic T must include an optional `value?: string | null` so the function can
 * determine whether an entry has a meaningful value.
 *
 * @param values - Array of entries to normalize. Each entry may include a
 *                 customFieldDefinition with `id` and `referenceId`.
 * @param preferredDefinitionIds - Set of custom field definition ids that should be
 *                                 preferred when choosing between entries sharing the same referenceId.
 * @returns A new array containing preserved entries without referenceId followed by
 *          one chosen entry per referenceId.
 */
function normalizeCustomFieldValuesByReferenceId(values, preferredDefinitionIds = new Set()) {
    const normalizedValues = [];
    const byReferenceId = new Map();
    // Iterate entries; preserve those without a referenceId and deduplicate those with one.
    for (const value of values ?? []) {
        const referenceId = value.customFieldDefinition?.referenceId;
        if (!referenceId) {
            // No referenceId: keep as-is and preserve original order for these entries.
            normalizedValues.push(value);
            continue;
        }
        const existingValue = byReferenceId.get(referenceId);
        if (!existingValue) {
            // First entry for this referenceId: tentatively store it.
            byReferenceId.set(referenceId, value);
            continue;
        }
        // Determine whether each candidate has a meaningful (non-empty) value.
        const nextHasMeaningfulValue = value.value != null && String(value.value).trim() !== "";
        const existingHasMeaningfulValue = existingValue.value != null && String(existingValue.value).trim() !== "";
        // Determine whether either candidate matches a preferred custom field definition id.
        const nextMatchesPreferredDefinition = preferredDefinitionIds.has(String(value.customFieldDefinition?.id ?? ""));
        const existingMatchesPreferredDefinition = preferredDefinitionIds.has(String(existingValue.customFieldDefinition?.id ?? ""));
        // If exactly one matches the preferred set, prefer that one.
        if (nextMatchesPreferredDefinition !== existingMatchesPreferredDefinition) {
            if (nextMatchesPreferredDefinition) {
                byReferenceId.set(referenceId, value);
            }
            continue;
        }
        // Otherwise prefer the one that has a meaningful value, or replace if existing has none.
        if (!existingHasMeaningfulValue || nextHasMeaningfulValue) {
            byReferenceId.set(referenceId, value);
        }
    }
    return [...normalizedValues, ...Array.from(byReferenceId.values())];
}
function calculateCustomFieldSumsForItems(reportingItems, preferredDefinitionIds = new Set()) {
    const customFieldSums = {};
    if (reportingItems.length === 0) {
        return { customFieldSums: {} };
    }
    for (const reportingItem of reportingItems ?? []) {
        for (const customField of normalizeCustomFieldValuesByReferenceId(reportingItem.customFields, preferredDefinitionIds)) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = getCustomFieldPublicId(customField.customFieldDefinition);
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    return { customFieldSums };
}
function calculateCustomFieldAveragesForItems(reportingItems, preferredDefinitionIds = new Set()) {
    if (reportingItems.length === 0) {
        return { customFieldAverages: {} };
    }
    const customFieldSums = {};
    for (const reportingItem of reportingItems ?? []) {
        for (const customField of normalizeCustomFieldValuesByReferenceId(reportingItem.customFields, preferredDefinitionIds)) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = getCustomFieldPublicId(customField.customFieldDefinition);
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    const customFieldAverages = {};
    for (const key in customFieldSums) {
        customFieldAverages[key] = customFieldSums[key] / reportingItems.length;
    }
    return { customFieldAverages };
}
function calculateCustomFieldSumsForProjects(projects, preferredDefinitionIds = new Set()) {
    const customFieldSums = {};
    if (projects.length === 0) {
        return { customFieldSums: {} };
    }
    for (const project of projects ?? []) {
        for (const customField of normalizeCustomFieldValuesByReferenceId(project.customFields, preferredDefinitionIds)) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = getCustomFieldPublicId(customField.customFieldDefinition);
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    return { customFieldSums };
}
function calculateCustomFieldAveragesForProjects(projects, preferredDefinitionIds = new Set()) {
    if (projects.length === 0) {
        return { customFieldAverages: {} };
    }
    const customFieldSums = {};
    for (const project of projects ?? []) {
        for (const customField of normalizeCustomFieldValuesByReferenceId(project.customFields, preferredDefinitionIds)) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = getCustomFieldPublicId(customField.customFieldDefinition);
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    const customFieldAverages = {};
    for (const key in customFieldSums) {
        customFieldAverages[key] = customFieldSums[key] / projects.length;
    }
    return { customFieldAverages };
}

function getProjectCompletionDates(project) {
    if (!project || !project.reportingItems) {
        return null;
    }
    const isProjectCompleted = project?.reportingItems?.every((item) => item.completionDate);
    const latest = isProjectCompleted
        ? project?.reportingItems
            .map((item) => item.completionDate)
            .filter(Boolean)
            .map((date) => dayjs(date))
            .filter((d) => d.isValid())
            .sort((a, b) => b.valueOf() - a.valueOf())[0]
        : null;
    return latest ? latest.toISOString() : null;
}

/**
 * generic solution to determine reporting days
 * @param params details for the reporting cycle, and the last updated date per oe
 * @returns reporting status
 */
function getReportingStatus(params) {
    const { endReportingDateInStringFormat, lastUpdatedDateInStringFormat, conditionUponWhichToReturnGreenByDefault, frequency, unit, } = params;
    if (!endReportingDateInStringFormat || !frequency || !unit) {
        return {
            color: COLORS[2],
            icon: ICONS[2],
            status: "Overdue",
            contrastTextColor: CONTRAST_TEXT_COLORS[2],
            muiIcon: "SentimentDissatisfied",
        };
    }
    if (!lastUpdatedDateInStringFormat) {
        return {
            color: COLORS[1],
            icon: ICONS[1],
            status: "Pending",
            contrastTextColor: CONTRAST_TEXT_COLORS[1],
            muiIcon: "SentimentSatisfied",
        };
    }
    if (conditionUponWhichToReturnGreenByDefault) {
        return {
            color: COLORS[0],
            icon: ICONS[0],
            status: "Updated",
            contrastTextColor: CONTRAST_TEXT_COLORS[0],
            muiIcon: "SentimentSatisfiedAlt",
        };
    }
    const endReportingDate = dayjs(endReportingDateInStringFormat);
    const lastUpdatedDate = dayjs(lastUpdatedDateInStringFormat);
    const startOfCurrentReportingCycle = endReportingDate.subtract(frequency, unit);
    const daysInCurrentReportingCycle = endReportingDate.diff(startOfCurrentReportingCycle, "days");
    const startOfPreviousReportingCycle = endReportingDate.subtract(2 * frequency, unit);
    const daysSinceStartOfPreviousCycle = endReportingDate.diff(startOfPreviousReportingCycle, "days");
    switch (true) {
        /**
         * case 1: if the lastUpdateDate is within the current cycle, the difference between the endDate and the last date will be less than the days in the current cycle
         * case 2: if the lastUpdateDate is within the previous cycle, the difference between the endDate and the last date will be less than the days in the previous cycle + current cycle
         * case 3: if the lastUpdateDate is not within the current or previous cycle, the difference between the endDate and the last date will be greater than the days in the previous cycle + current cycle
         */
        case dayjs(endReportingDate).diff(lastUpdatedDate, "days", true) <=
            daysInCurrentReportingCycle:
            return {
                color: COLORS[0],
                icon: ICONS[0],
                status: "Updated",
                contrastTextColor: CONTRAST_TEXT_COLORS[0],
                muiIcon: "SentimentSatisfiedAlt",
            };
        case dayjs(endReportingDate).diff(lastUpdatedDate, "days", true) <=
            daysSinceStartOfPreviousCycle:
            return {
                color: COLORS[1],
                icon: ICONS[1],
                status: "Pending",
                contrastTextColor: CONTRAST_TEXT_COLORS[1],
                muiIcon: "SentimentSatisfied",
            };
        default:
            return {
                color: COLORS[2],
                icon: ICONS[2],
                status: "Overdue",
                contrastTextColor: CONTRAST_TEXT_COLORS[2],
                muiIcon: "SentimentDissatisfied",
            };
    }
}

function validateBasicItemForm(item, configuration, localizedMessages) {
    const errors = {};
    for (const field of configuration?.fieldsDefinition?.filter((f) => f.purpose === "itemBasic" && f.enabled && f.isMandatory) ?? []) {
        const fieldValue = item[field.fieldName];
        if (fieldValue === undefined || fieldValue === "" || fieldValue === null) {
            errors[`${field.fieldName}`] = {
                type: "required",
                message: localizedMessages?.fieldRequired || "This field is required",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
        checkFieldLength({
            fieldName: field.fieldName,
            customFieldKey: `basicItems.{idx}.${field.fieldName}`,
            value: fieldValue,
            errors: errors,
        });
        if (item.endDate && !dayjs(item.endDate).isValid()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
        if (item.startDate && !dayjs(item.startDate).isValid()) {
            errors[`startDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
        if (item.endDate &&
            item.startDate &&
            new Date(item.endDate).getTime() < new Date(item.startDate).getTime()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.endDateAfterStartDate ||
                    "End date should be after start date",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
    }
    const definedEntries = Object.entries(errors).filter(([, v]) => v !== undefined);
    return Object.fromEntries(definedEntries);
}

function validateBasicProjectForm(project, configuration, localizedMessages) {
    const errors = {};
    for (const field of configuration?.fieldsDefinition?.filter((f) => f.purpose === "projectBasic" && f.enabled && f.isMandatory) ?? []) {
        const fieldValue = project[field.fieldName];
        if (fieldValue === undefined || fieldValue === "" || fieldValue === null) {
            errors[`${field.fieldName}`] = {
                type: "required",
                message: localizedMessages?.fieldRequired || "This field is required",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
        checkFieldLength({
            fieldName: field.fieldName,
            customFieldKey: `projects.{idx}.${field.fieldName}`,
            value: fieldValue,
            errors: errors,
        });
        if (project.endDate && !dayjs(project.endDate).isValid()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
        if (project.startDate && !dayjs(project.startDate).isValid()) {
            errors[`startDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
        if (project.endDate &&
            project.startDate &&
            new Date(project.endDate).getTime() <
                new Date(project.startDate).getTime()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.endDateAfterStartDate ||
                    "End date should be after start date",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
    }
    const definedEntries = Object.entries(errors).filter(([, v]) => v !== undefined);
    return Object.fromEntries(definedEntries);
}

function validateConfiguration(configuration, projects, basicItems, toolUsers, clientUsers, localizedMessages, notification) {
    const errors = {};
    const requiredMessage = localizedMessages?.fieldRequired || "This field is required";
    const invalidDateMessage = localizedMessages?.dateNotValid || "This date is not valid";
    const endBeforeStartMessage = localizedMessages?.endDateAfterStartDate ||
        "End date should be after start date";
    const setError = (key, type, message, tabContainingErrors) => {
        errors[key] = {
            type,
            message,
            tabContainingErrors: tabContainingErrors ?? "",
        };
    };
    const requireField = (key, tab) => setError(key, "required", requiredMessage, tab);
    // Reporting Structure
    if (!configuration.programName) {
        requireField("programName", VIEW_NAMES[0]);
    }
    checkFieldLength({
        fieldName: "programName",
        value: configuration.programName,
        errors,
    });
    checkFieldLength({
        fieldName: "programOwner",
        value: configuration.programOwner,
        errors,
    });
    if (!configuration.projectName) {
        requireField("projectName", VIEW_NAMES[0]);
    }
    if (!configuration.itemName) {
        requireField("itemName", VIEW_NAMES[0]);
    }
    // Reporting Period
    if (!configuration.reportingCycle?.unit) {
        requireField("unit", VIEW_NAMES[1]);
    }
    if (!configuration.reportingCycle?.startReportingDate) {
        requireField("startReportingDate", VIEW_NAMES[1]);
    }
    if (!configuration.reportingCycle?.endReportingDate) {
        requireField("endReportingDate", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.startReportingDate &&
        !dayjs(configuration.reportingCycle?.startReportingDate).isValid()) {
        setError("startReportingDate", "invalid", invalidDateMessage, VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.endReportingDate &&
        !dayjs(configuration.reportingCycle?.endReportingDate).isValid()) {
        setError("endReportingDate", "invalid", invalidDateMessage, VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.endReportingDate &&
        configuration.reportingCycle?.startReportingDate &&
        new Date(configuration.reportingCycle?.endReportingDate).getTime() <
            new Date(configuration.reportingCycle?.startReportingDate).getTime()) {
        setError("endReportingDate", "invalid", endBeforeStartMessage, VIEW_NAMES[1]);
    }
    if (!configuration.reportingCycle?.frequency) {
        requireField("frequency", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.unit === "week" &&
        !configuration.reportingCycle?.weeklyReportingDay) {
        requireField("weeklyReportingDay", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.unit === "month" &&
        !configuration.reportingCycle?.monthlyReportingDay) {
        requireField("monthlyReportingDay", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.unit === "year" &&
        !configuration.reportingCycle?.yearlyReportingDay) {
        requireField("yearlyReportingDay", VIEW_NAMES[1]);
    }
    if (configuration.fieldsDefinition?.find((f) => f.fieldName === "category" && f.enabled && f.label.trim() === "")) {
        requireField("categoryDefinition", VIEW_NAMES[2]);
    }
    const categoryFieldDef = configuration.fieldsDefinition?.find((f) => f.fieldName === "category" && f.enabled);
    if (categoryFieldDef && categoryFieldDef.label.length > 30) {
        setError("categoryDefinition", "maxLength", "Category field label must be less than 30 characters", VIEW_NAMES[2]);
    }
    if (configuration.fieldsDefinition?.find((f) => f.fieldName === "dropdownFieldOne" && f.enabled && f.label.trim() === "")) {
        requireField("dropdownFieldOneDefinition", VIEW_NAMES[3]);
    }
    const dropdownFieldDef = configuration.fieldsDefinition?.find((f) => f.fieldName === "dropdownFieldOne" && f.enabled);
    if (dropdownFieldDef && dropdownFieldDef.label.length > 30) {
        setError("dropdownFieldOneDefinition", "maxLength", "Dropdown field label must be less than 30 characters", VIEW_NAMES[3]);
    }
    const secondCategoryFieldDef = configuration.fieldsDefinition?.find((f) => f.fieldName === "secondCategory" && f.enabled);
    if (secondCategoryFieldDef && secondCategoryFieldDef.label.length > 30) {
        setError("secondCategoryDefinition", "maxLength", "Secondary category field label must be less than 30 characters", VIEW_NAMES[2]);
    }
    // Custom fields
    configuration.customFields?.forEach((field, index) => {
        if (field.flag === "delete")
            return;
        if (!field.label || field.label.trim() === "") {
            requireField(`customFields.${index}.label`, VIEW_NAMES[4]);
        }
        checkFieldLength({
            iterableFieldName: "customFields.{idx}.label",
            value: field.label,
            idx: index,
            tabContainingErrors: VIEW_NAMES[4],
            errors,
        });
        if (field.type === "Select" && field.enabled) {
            field.options?.forEach((option, optionIndex) => {
                if ((option.name ?? "").trim() === "") {
                    requireField(`customFields.${index}.options.${optionIndex}.name`, VIEW_NAMES[4]);
                }
                checkFieldLength({
                    iterableFieldName: "customFields.{idx}.options.{idx}.name",
                    value: option.name,
                    idx: [index, optionIndex],
                    tabContainingErrors: VIEW_NAMES[4],
                    errors,
                });
            });
        }
    });
    const fieldsDef = configuration.fieldsDefinition ?? [];
    const validateListWithFieldDef = (list, listKey, tabTitle, errorMessage = "All names must be filled in", fieldInConfiguration) => {
        list?.forEach((item, idx) => {
            const fieldDef = fieldInConfiguration
                ? fieldsDef.find((f) => f.fieldInConfiguration === fieldInConfiguration)
                : undefined;
            if (fieldInConfiguration && !fieldDef?.enabled) {
                return;
            }
            if (item.name.trim() === "") {
                setError(`${listKey}.${idx}.name`, "required", errorMessage, tabTitle);
            }
            if (item.name.length > 30) {
                setError(`${listKey}.${idx}.name`, "invalid", "Name must be less than 30 characters", tabTitle);
            }
        });
    };
    validateListWithFieldDef(configuration.categoryDefinition, "categoryDefinition", VIEW_NAMES[2], "All category names must be filled in", "categoryDefinition");
    validateListWithFieldDef(configuration.dropdownFieldOneDefinition, "dropdownFieldOneDefinition", VIEW_NAMES[3], "All fields must be filled in", "dropdownFieldOneDefinition");
    validateListWithFieldDef(configuration.secondCategoryDefinition, "secondCategoryDefinition", VIEW_NAMES[2], "All category names must be filled in", "secondCategoryDefinition");
    validateListWithFieldDef(configuration.statusDefinition, "statusDefinition", VIEW_NAMES[4], "All status names must be filled in", "statusDefinition");
    validateListWithFieldDef(configuration.degreeOfCompletionDefinition, "degreeOfCompletionDefinition", VIEW_NAMES[4], "All degree of completion names must be filled in", "degreeOfCompletionDefinition");
    projects?.forEach((project, idx) => {
        if (project.flag === "delete")
            return;
        for (const field of fieldsDef.filter((f) => f.purpose === "projectBasic" && f.enabled && f.isMandatory)) {
            const value = project[field.fieldName];
            switch (field.type) {
                case "Text":
                case "Number":
                    checkFieldLength({
                        iterableFieldName: `projects.{idx}.${field.fieldName}`,
                        idx,
                        errors,
                        value,
                        tabContainingErrors: VIEW_NAMES[5],
                    });
                    checkRequiredField({
                        iterableFieldName: `projects.{idx}.${field.fieldName}`,
                        idx,
                        errors,
                        value,
                        tabContainingErrors: VIEW_NAMES[5],
                    });
                    break;
                case "Date":
                    if (project[field.fieldName] &&
                        !dayjs(project[field.fieldName]).isValid()) {
                        setError(`projects.${idx}.${field.fieldName}`, "invalid", invalidDateMessage, VIEW_NAMES[5]);
                        break;
                    }
            }
        }
        if (project.endDate &&
            project.startDate &&
            new Date(project.endDate).getTime() <
                new Date(project.startDate).getTime()) {
            setError(`projects.${idx}.endDate`, "invalid", endBeforeStartMessage, VIEW_NAMES[5]);
        }
    });
    basicItems?.forEach((basicItem, idx) => {
        if (basicItem.flag === "delete")
            return;
        for (const field of fieldsDef.filter((f) => f.purpose === "itemBasic" && f.enabled && f.isMandatory)) {
            const value = basicItem[field.fieldName];
            if (value === undefined || value === "" || value === null) {
                setError(`basicItems.${idx}.${field.fieldName}`, "required", requiredMessage, VIEW_NAMES[6]);
            }
            if (!basicItem.projectIds?.length) {
                setError(`basicItems.${idx}.projectIds`, "invalid", "This field is required", VIEW_NAMES[6]);
            }
            if (basicItem.endDate && !dayjs(basicItem.endDate).isValid()) {
                setError(`basicItems.${idx}.endDate`, "invalid", invalidDateMessage, VIEW_NAMES[6]);
            }
            if (basicItem.startDate && !dayjs(basicItem.startDate).isValid()) {
                setError(`basicItems.${idx}.startDate`, "invalid", invalidDateMessage, VIEW_NAMES[6]);
            }
            checkFieldLength({
                iterableFieldName: `basicItem.{idx}.budget`,
                idx,
                errors,
                value,
                tabContainingErrors: VIEW_NAMES[8],
            });
            checkFieldLength({
                iterableFieldName: `basicItems.{idx}.${field.fieldName}`,
                value: value,
                idx,
                errors,
            });
        }
        if (basicItem.customId && basicItem.customId?.length > 15) {
            setError(`basicItems.${idx}.customId`, "invalid", "Custom ID must be less than 15 characters", VIEW_NAMES[6]);
        }
        if (basicItem.endDate &&
            basicItem.startDate &&
            new Date(basicItem.endDate).getTime() <
                new Date(basicItem.startDate).getTime()) {
            setError(`basicItems.${idx}.endDate`, "invalid", endBeforeStartMessage, VIEW_NAMES[6]);
        }
    });
    toolUsers?.forEach((user, item) => {
        if (user.flag === "delete")
            return;
        if ((clientUsers?.filter((u) => u.email === user.email && u.id !== user.id)
            ?.length ?? 0) > 0) {
            setError(`toolUsers.${item}.email`, "invalid", "This email is already used by a client user", VIEW_NAMES[7]);
        }
        if (!user.email) {
            errors[`toolUsers.${item}.email`] = {
                type: "required",
                message: "This field is required",
                tabContainingErrors: VIEW_NAMES[7],
            };
        }
        if (!user.permissions?.role) {
            errors[`toolUsers.${item}.permissions.role`] = {
                type: "required",
                message: "This field is required",
                tabContainingErrors: VIEW_NAMES[7],
            };
        }
    });
    // validate only if the notification is active.
    if (notification?.active) {
        if (!notification?.title) {
            requireField("notification.title", VIEW_NAMES[8]);
        }
        if (!notification?.text) {
            requireField("notification.text", VIEW_NAMES[8]);
        }
        if (!notification?.recipients || notification.recipients.length === 0) {
            requireField("notification.recipients", VIEW_NAMES[8]);
        }
    }
    if (notification?.title && notification.title.length > 64) {
        setError("notification.title", "maxLength", "The notification title must not be longer than 64 characters", VIEW_NAMES[8]);
    }
    if (notification?.text && notification.text.length > 256) {
        setError("notification.text", "maxLength", "The notification text must not be longer than 256 characters", VIEW_NAMES[8]);
    }
    return errors;
}

function validateReportUpdateForm(data, configuration, localizedMessages) {
    const errors = {};
    const requiredMessage = localizedMessages?.fieldRequired || "This field is required";
    const setError = (path, message = requiredMessage) => {
        errors[path] = { type: "required", message };
    };
    const isRequired = (fieldKey, area) => getFieldValidation(configuration, fieldKey, area) === true;
    // project-level fields
    const projectChecks = [
        {
            present: Boolean(data.managementSummary),
            value: data.managementSummary,
            field: "managementSummary",
            path: "managementSummary",
        },
        {
            present: Boolean(data.challenges),
            value: data.challenges,
            field: "challenges",
            path: "challenges",
        },
        {
            present: Boolean(data.achievements),
            value: data.achievements,
            field: "achievements",
            path: "achievements",
        },
        {
            present: Boolean(data.nextSteps),
            value: data.nextSteps,
            field: "nextSteps",
            path: "nextSteps",
        },
        {
            present: data.spentBudget != null,
            value: data.spentBudget,
            field: "spentBudget",
            path: "spentBudget",
            nullIsAllowed: false,
        },
    ];
    for (const check of projectChecks) {
        if (!check.present && isRequired(check.field, "projectReporting")) {
            setError(check.path);
        }
        checkFieldLength({
            fieldName: check.field,
            value: check.value,
            errors: errors,
        });
    }
    for (const cf of data.customFields ?? []) {
        if (cf.customFieldDefinition?.mandatory &&
            (cf.value == null || cf.value === "")) {
            setError(`customFields.[${data.customFields?.indexOf(cf)}].value`);
        }
        // Look up the custom field definition from configuration to get the type
        // since cf.customFieldDefinition might not be fully populated from the request
        const cfDefId = typeof cf.customFieldDefinition === "object"
            ? cf.customFieldDefinition?.id
            : cf.customFieldDefinition;
        if (cfDefId && configuration?.customFields) {
            const cfDef = configuration.customFields.find((def) => String(def.id) === String(cfDefId));
            if (cfDef?.type) {
                checkFieldLength({
                    customFieldKey: `customFields.${cfDef.type}`,
                    fieldName: `customFields.[${data.customFields?.indexOf(cf)}].value`,
                    value: cf.value,
                    errors: errors,
                });
            }
        }
    }
    // item-level checks — skip items that don't belong to the active dimension
    // when the form was submitted for a single dimension (maturity use case only).
    for (const reportingItem of data.reportingItems ?? []) {
        if (data._reportDimensionFilter &&
            (reportingItem.basicItem?.dimension || "No Dimension") !==
                data._reportDimensionFilter) {
            continue;
        }
        const idx = data.reportingItems?.indexOf(reportingItem) ?? 0;
        const basePath = (p) => `reportingItems.[${idx}].${p}`;
        const iterableBasePath = (p) => `reportingItems.[{idx}].${p}`;
        const isEmpty = (v) => v == null || v === "";
        // status: required either when not pmo, or when pmo & overall level
        const statusRequired = isEmpty(reportingItem?.status) &&
            isRequired("status", "itemReporting") &&
            (configuration?.useCase !== "pmo" ||
                (configuration?.useCase === "pmo" &&
                    configuration?.statusReportingLevel === "overall"));
        if (statusRequired) {
            setError(basePath("status"));
        }
        // when pmo & category level -> three semaphore fields required
        if (configuration?.useCase === "pmo" &&
            configuration.statusReportingLevel === "category") {
            if (isEmpty(reportingItem?.semaphoreStatus1)) {
                setError(basePath("semaphoreStatus1"));
            }
            if (isEmpty(reportingItem?.semaphoreStatus2)) {
                setError(basePath("semaphoreStatus2"));
            }
            if (isEmpty(reportingItem?.semaphoreStatus3)) {
                setError(basePath("semaphoreStatus3"));
            }
        }
        // completion
        if (isEmpty(reportingItem?.degreeOfCompletion) &&
            isRequired("completion", "itemReporting")) {
            setError(basePath("degreeOfCompletion"));
        }
        // progress
        if (reportingItem?.progress == null &&
            isRequired("progress", "itemReporting")) {
            setError(basePath("progress"));
        }
        // comment
        if (isEmpty(reportingItem?.comment) &&
            isRequired("comment", "itemReporting")) {
            setError(basePath("comment"));
        }
        if (isEmpty(reportingItem?.answer) &&
            isRequired("answer", "itemReporting")) {
            setError(basePath("answer"));
        }
        checkFieldLength({
            iterableFieldName: iterableBasePath("comment"),
            idx,
            value: reportingItem?.comment,
            errors: errors,
        });
        // attachments (special message)
        if ((!reportingItem?.attachments || reportingItem.attachments.length === 0) &&
            isRequired("attachments", "itemReporting")) {
            setError(basePath("attachments"), "At least one attachment is required");
        }
        let customFieldIdx = 0;
        for (const cf of reportingItem.customFields ?? []) {
            if (cf.customFieldDefinition?.mandatory &&
                (cf.value == null || cf.value === "")) {
                setError(basePath(`customFields.[${reportingItem.customFields?.indexOf(cf)}].value`));
            }
            // Look up the custom field definition from configuration to get the type
            // since cf.customFieldDefinition might not be fully populated from the request
            const cfDefId = typeof cf.customFieldDefinition === "object"
                ? cf.customFieldDefinition?.id
                : cf.customFieldDefinition;
            if (cfDefId && configuration?.customFields) {
                const cfDef = configuration.customFields.find((def) => String(def.id) === String(cfDefId));
                if (cfDef?.type) {
                    checkFieldLength({
                        customFieldKey: `customFields.${cfDef.type}`,
                        iterableFieldName: basePath(`customFields.[${customFieldIdx}].value`),
                        idx,
                        value: cf.value,
                        errors: errors,
                    });
                }
            }
            customFieldIdx++;
        }
    }
    const definedEntries = Object.entries(errors).filter(([, v]) => v !== undefined);
    return Object.fromEntries(definedEntries);
}

function getReportingDetails(reportingDetails) {
    const { unit = "week", frequency } = reportingDetails;
    if (!frequency) {
        reportingDetails.switchReportingCycleDate = null;
        reportingDetails.startReportingCycleDate = null;
        reportingDetails.endReportingCycleDate = null;
        reportingDetails.startReportingDate = null;
        reportingDetails.endReportingDate = null;
        reportingDetails.unit = "week";
        return;
    }
    const today = getReportingCycleDate();
    const startReportingDate = getReportingCycleDate(reportingDetails.startReportingDate);
    const baseDate = today.isAfter(startReportingDate)
        ? today
        : startReportingDate;
    if (unit === "week") {
        const todaysDay = baseDate.day();
        if (todaysDay > Number(reportingDetails.weeklyReportingDay)) {
            reportingDetails.switchReportingCycleDate = baseDate
                .add(frequency, unit)
                .day(Number(reportingDetails.weeklyReportingDay))
                .format("YYYY-MM-DD");
        }
        else {
            reportingDetails.switchReportingCycleDate = baseDate
                .add(frequency - 1, unit)
                .day(Number(reportingDetails.weeklyReportingDay))
                .format("YYYY-MM-DD");
        }
    }
    else if (unit === "month") {
        const monthlyDay = Number(reportingDetails.monthlyReportingDay);
        if (monthlyDay === 31 || monthlyDay === 30) {
            reportingDetails.switchReportingCycleDate = baseDate
                .add(frequency - 1, unit)
                .endOf("month")
                .format("YYYY-MM-DD");
        }
        else {
            if (today.date() > monthlyDay) {
                reportingDetails.switchReportingCycleDate = baseDate
                    .add(frequency, unit)
                    .date(monthlyDay)
                    .format("YYYY-MM-DD");
            }
            else {
                reportingDetails.switchReportingCycleDate = baseDate
                    .add(frequency - 1, unit)
                    .date(monthlyDay)
                    .format("YYYY-MM-DD");
            }
        }
    }
    else {
        reportingDetails.switchReportingCycleDate = formatReportingCycleDate(reportingDetails.yearlyReportingDay);
    }
    reportingDetails.startReportingCycleDate = formatReportingCycleDate(baseDate);
    reportingDetails.endReportingCycleDate =
        reportingDetails.switchReportingCycleDate;
}

function calculateItemSemaphoreStatus(reportingItem, semaphoreStatusDefinition) {
    // since the definitions are the same for all items, we just take the first one
    // if we decide to make the colors customizable this logic needs to be adapted too
    const def = Array.isArray(semaphoreStatusDefinition)
        ? semaphoreStatusDefinition[0]
        : undefined;
    if (!def)
        return undefined;
    const statuses = [
        reportingItem.semaphoreStatus1,
        reportingItem.semaphoreStatus2,
        reportingItem.semaphoreStatus3,
    ].filter(Boolean);
    const matchesAny = (name) => {
        return name != null && statuses.some((s) => s === name);
    };
    if (matchesAny(def.high?.name)) {
        return def.high;
    }
    if (matchesAny(def.medium?.name)) {
        return def.medium;
    }
    if (matchesAny(def.low?.name)) {
        return def.low;
    }
    return undefined;
}

export { ASSESSMENT_CONFIG, AUDIT_CONFIG, AUDIT_VIEW_CONFIG, CHARACTER_LIMITATIONS, DEFAULT_CONFIG, MATURITY_CONFIG, MATURITY_VIEW_CONFIG, PMO_CONFIG, PMO_VIEW_CONFIG, REPORTING_CYCLE_DATE_FORMAT, ROLLOUT_CONFIG, ROLLOUT_VIEW_CONFIG, VIEW_NAMES, buildAccessString, calculateAverageProgress, calculateCustomFieldAveragesForItems, calculateCustomFieldAveragesForProjects, calculateCustomFieldSumsForItems, calculateCustomFieldSumsForProjects, calculateDimensionFormCompletion, calculateItemSemaphoreStatus, calculateLastUpdateDateForProject, calculateMaturityGauge, calculateNumberOfOverdueSubEntities, calculateOverallCompletionDegreeCounts, calculateOverallDegreeOfCompletion, calculateOverallDegreeOfCompletionCount, calculateOverallSeverityCounts, calculateOverallStatus, calculateOverallStatusCount, calculateProjectBudgetDetails, calculateSemaphoreStatus, calculateTotalBudgetDetails, canUserPerformOperation, checkField, checkFieldLength, checkRequiredField, countAccumulatedItemsOverReportingCycle, countCompletedItemsOverReportingCycle, countCompletedItemsPerCategory, countCompletedItemsPerCategoryLevel, countCompletedProjectsOverReportingCycle, countCompletedProjectsPerCategory, countCompletedReportingItems, countCompletionDegreesOfProjects, countOverdueProjects, countSemaphoreStatusesOfProjects, countSemaphoreStatusesOfReportingItems, countStatusesOfProjects, determineUserRole, findDropdownOption, formatReportingCycleDate, getAssessmentConfig, getAuditConfig, getContrastColor, getCurrentMaturityIndexForItems, getCurrentMaturityLabelForItems, getCustomFieldAggregationComponentId, getCustomFieldComponentId, getCustomFieldPublicId, getCustomFieldTableColumnFieldName, getDefaultConfig, getDropdownOptionColor, getDropdownOptionName, getDropdownOptionStoredValue, getFieldValidation, getFirstDropdownOptionStoredValue, getLegacyCustomFieldTableColumnFieldName, getMaturityConfig, getPmoConfig, getProjectCompletionDates, getReportingCycleDate, getReportingDetails, getReportingStatus, getRolloutConfig, localizeStructuredSampleValue, parsePermission, validateBasicItemForm, validateBasicProjectForm, validateConfiguration, validateReportUpdateForm };
